import { useState, useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Analytics } from '@vercel/analytics/react';

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const lsGet = async (key) => { try { const v = localStorage.getItem(key); return v ? { value: v } : null; } catch(e) { return null; } };
const lsSet = async (key, value) => { try { localStorage.setItem(key, value); return true; } catch(e) { return null; } };
const lsDel = async (key) => { try { localStorage.removeItem(key); return true; } catch(e) { return null; } };
const callAnthropic = async (body) => {
  const res = await fetch("/api/anthropic", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  return res.json();
};

const INTERVAL_FREQS = new Set(["Every other day","Once weekly","Twice weekly","Three times weekly","Once monthly"]);
const PRN_FREQS = new Set(["As needed daily","As needed twice daily","As needed at bedtime","As needed every 10 minutes","As needed every 2 hours","As needed every 3 hours","As needed every 4 hours","As needed every 4-6 hours","As needed every 6 hours","As needed every 6-8 hours","As needed every 8 hours","As needed every 12 hours","As needed every 24 hours","As needed every 48 hours"]);
const isInterval = (f) => INTERVAL_FREQS.has(f||"");
const isPRN = (f) => PRN_FREQS.has(f||"") || /\bprn\b|as\s+needed/i.test(f||"");
const isPRNWithoutInterval = (f) => isPRN(f) && !/every\s*\d|daily|bedtime/i.test(f||"");
const isHS = (f) => !isPRN(f) && /\bbedtime\b|\bnightly\b|\bhs\b|\bat night\b|\bbefore bed\b/i.test(f||"") && !/in the morning/i.test(f||"");
const isEVENING = (f) => !isPRN(f) && !isHS(f) && /in the evening|\bevening\b/i.test(f||"") && !/in the morning/i.test(f||"");
const isMORNING = (f) => !isPRN(f) && !isHS(f) && !isEVENING(f) && /in the morning|\bmorning\b/i.test(f||"");
const isHSGroup = (f) => isHS(f) || isEVENING(f);

const OTHER_FREQ = "Other — type your own";
const FREQ_OPTIONS = [
  { group:"Daily", label:"Once daily", doses:1 },
  { group:"Daily", label:"Twice daily", doses:2 },
  { group:"Daily", label:"Three times daily", doses:3 },
  { group:"Daily", label:"Four times daily", doses:4 },
  { group:"Daily", label:"Five times daily", doses:5 },
  { group:"Daily", label:"Six times daily", doses:6 },
  { group:"Every X hours", label:"Every 2 hours", doses:12 },
  { group:"Every X hours", label:"Every 4 hours", doses:6 },
  { group:"Every X hours", label:"Every 6 hours", doses:4 },
  { group:"Every X hours", label:"Every 8 hours", doses:3 },
  { group:"Every X hours", label:"Every 12 hours", doses:2 },
  { group:"Every X hours", label:"Every 24 hours", doses:1 },
  { group:"Every X hours", label:"Every 48 hours", doses:1 },
  { group:"With meals", label:"With breakfast", doses:1 },
  { group:"With meals", label:"With lunch", doses:1 },
  { group:"With meals", label:"With dinner", doses:1 },
  { group:"With meals", label:"With breakfast and dinner", doses:2 },
  { group:"With meals", label:"With every meal", doses:3 },
  { group:"With meals", label:"With meals and at bedtime", doses:4 },
  { group:"Time of day", label:"In the morning", doses:1 },
  { group:"Time of day", label:"In the morning and in the evening", doses:2 },
  { group:"Time of day", label:"In the morning and at bedtime", doses:2 },
  { group:"Time of day", label:"In the evening", doses:1 },
  { group:"Time of day", label:"At bedtime", doses:1 },
  { group:"Time of day", label:"Twice daily and at bedtime", doses:3 },
  { group:"As needed (PRN)", label:"As needed daily", doses:1 },
  { group:"As needed (PRN)", label:"As needed twice daily", doses:2 },
  { group:"As needed (PRN)", label:"As needed at bedtime", doses:1 },
  { group:"As needed (PRN)", label:"As needed every 10 minutes", doses:1 },
  { group:"As needed (PRN)", label:"As needed every 2 hours", doses:12 },
  { group:"As needed (PRN)", label:"As needed every 3 hours", doses:8 },
  { group:"As needed (PRN)", label:"As needed every 4 hours", doses:6 },
  { group:"As needed (PRN)", label:"As needed every 4-6 hours", doses:4 },
  { group:"As needed (PRN)", label:"As needed every 6 hours", doses:4 },
  { group:"As needed (PRN)", label:"As needed every 6-8 hours", doses:3 },
  { group:"As needed (PRN)", label:"As needed every 8 hours", doses:3 },
  { group:"As needed (PRN)", label:"As needed every 12 hours", doses:2 },
  { group:"As needed (PRN)", label:"As needed every 24 hours", doses:1 },
  { group:"As needed (PRN)", label:"As needed every 48 hours", doses:1 },
  { group:"Weekly / Other", label:"Every other day", doses:1 },
  { group:"Weekly / Other", label:"Once weekly", doses:1 },
  { group:"Weekly / Other", label:"Twice weekly", doses:2 },
  { group:"Weekly / Other", label:"Three times weekly", doses:3 },
  { group:"Weekly / Other", label:"Once monthly", doses:1 },
  { group:"Other", label:"As directed", doses:1 },
  { group:"Other", label:OTHER_FREQ, doses:1 },
];
const FREQ_BY_GROUP = FREQ_OPTIONS.reduce((a,f) => { (a[f.group]=a[f.group]||[]).push(f); return a; }, {});

const ROUTE_PAT = [
  { r:"G-tube", p:/gastrostomy|g-tube|\bgtube\b|peg tube/i },
  { r:"SL", p:/sublingual|under the tongue|\bsl\b/i },
  { r:"Topical", p:/topical|\bapply\b|cream|ointment|\bgel\b|patch|transdermal/i },
  { r:"Inhaled", p:/inhale|inhaler|nebulizer|\bpuff\b|nasal spray/i },
  { r:"Injectable", p:/inject|injection|subcutaneous|intramuscular|\bim\b|\bsc\b|\bsq\b/i },
  { r:"Eye/Ear", p:/ophthalmic|otic|\beye drops\b|\bear drops\b/i },
  { r:"Rectal", p:/rectal|suppository|per rectum/i },
];
const getRoute = (m) => { const t=m.name+" "+m.freq+" "+(m.qty||""); for(const {r,p} of ROUTE_PAT) if(p.test(t)) return r; return "PO"; };

function cleanNotes(raw) {
  if(!raw) return "";
  return raw
    .replace(/Duration:\s*\d+\s*Days?\.?/gi,"")
    .replace(/Refills?:\s*\d+\.?/gi,"")
    .replace(/Pickup\s+at\s+[^.]+\.?/gi,"")
    .replace(/Follow\s+(up\s+)?with\s+[^.]+\.?/gi,"")
    .replace(/Over the counter\.?/gi,"")
    .replace(/^as\s+needed\s+for\s*/i,"")  // strip leading "as needed for"
    .replace(/^for\s+for\s*/i,"for ")       // fix "for for" duplication
    .replace(/\s{2,}/g," ").trim();
}

function normalizeFreq(raw) {
  if(!raw) return null;
  // Strip ICD-10 codes (e.g. J43.2, I10, E11.9) from freq string
  const s = raw.trim().replace(/\b[A-Z]\d{2,3}\.?\d{0,2}\b/g, "").trim();
  // Exact match passthrough — if AI returns our label verbatim, use it directly
  const EXACT = ["Once daily","Twice daily","Three times daily","Four times daily","Five times daily","Six times daily",
    "Every 2 hours","Every 4 hours","Every 6 hours","Every 8 hours","Every 12 hours","Every 24 hours","Every 48 hours",
    "With breakfast","With lunch","With dinner","With breakfast and dinner","With every meal","With meals and at bedtime",
    "In the morning","In the morning and in the evening","In the morning and at bedtime","In the evening","At bedtime","Twice daily and at bedtime",
    "As needed daily","As needed twice daily","As needed at bedtime","As needed every 2 hours","As needed every 3 hours","As needed every 4 hours","As needed every 4-6 hours",
    "As needed every 6 hours","As needed every 6-8 hours","As needed every 8 hours","As needed every 12 hours","As needed every 24 hours","As needed every 48 hours",
    "Every other day","Once weekly","Twice weekly","Three times weekly","Once monthly"];
  const exactMatch = EXACT.find(e => e.toLowerCase() === s.toLowerCase());
  if(exactMatch) return {freq:exactMatch, max:null, timing:null};
  const isPrnRaw = /as\s*needed|if\s*needed|\bprn\b/i.test(s);
  if(isPrnRaw) {
    if(/every\s*10\s*min/i.test(s)) {
      const maxMatch = s.match(/up\s*to\s*(\d+)\s*dose/i);
      return {freq:"As needed every 10 minutes",max:maxMatch?parseInt(maxMatch[1]):null,timing:null};
    }
    if(/every\s*2\s*hour/i.test(s)) return {freq:"As needed every 2 hours",max:null,timing:null};
    if(/every\s*3\s*hour/i.test(s)) return {freq:"As needed every 3 hours",max:null,timing:null};
    if(/every\s*4\s*-?\s*6\s*hour/i.test(s)) return {freq:"As needed every 4-6 hours",max:null,timing:null};
    if(/every\s*4\s*hour/i.test(s)) return {freq:"As needed every 4 hours",max:null,timing:null};
    if(/every\s*6\s*-?\s*8\s*hour/i.test(s)) return {freq:"As needed every 6-8 hours",max:null,timing:null};
    if(/every\s*12\s*hour/i.test(s)) return {freq:"As needed every 12 hours",max:null,timing:null};
    if(/every\s*8\s*hour/i.test(s)) return {freq:"As needed every 8 hours",max:null,timing:null};
    if(/every\s*6\s*hour/i.test(s)) return {freq:"As needed every 6 hours",max:null,timing:null};
    if(/every\s*24\s*hour/i.test(s)) return {freq:"As needed every 24 hours",max:1,timing:null};
    if(/every\s*48\s*hour/i.test(s)) return {freq:"As needed every 48 hours",max:1,timing:null};
    if(/bedtime|\bhs\b/i.test(s)) return {freq:"As needed at bedtime",max:1,timing:"bedtime"};
    if(/daily|once|one\s*time/i.test(s)) return {freq:"As needed daily",max:1,timing:null};
    let max = null;
    if(/four|4\s*times/i.test(s)) max=4;
    else if(/three|3\s*times/i.test(s)) max=3;
    else if(/twice|two\s*times|2\s*times/i.test(s)) return {freq:"As needed twice daily",max:2,timing:null};
    const timing = /evening/i.test(s)?"evening":null;
    if(max || timing) return {freq:"As needed daily",max,timing};
    // Catch-all: "as needed" or "if needed" with no specific interval → As needed daily
    return {freq:"As needed daily",max:null,timing:null};
  }
  // "as directed" / "see instructions" → recognized frequency
  if(/as\s*directed|see\s*instructions/i.test(s)) return {freq:"As directed",max:null,timing:null};
  
  // Check for compound timing patterns FIRST (before individual times)
  if(/morning.*noon.*evening|morning.*afternoon.*evening|morning.*midday.*evening|morning.*at\s*noon.*evening/i.test(s)) return {freq:"Three times daily",max:null,timing:null};
  if(/morning.*and.*evening|morning.*night|in the morning.*in the evening/i.test(s)) return {freq:"In the morning and in the evening",max:null,timing:null};
  if(/morning.*and.*bedtime|morning.*before\s*bed|morning.*at\s*bedtime/i.test(s)) return {freq:"In the morning and at bedtime",max:null,timing:null};
  
  // Then check individual times
  if(/in the evening|\bevening\b/i.test(s)&&!/meal|food/i.test(s)) {
    if(/twice|two\s*times|bid/i.test(s)) return {freq:"Twice daily and at bedtime",max:null,timing:null};
    return {freq:"In the evening",max:null,timing:null};
  }
  if(/in the morning|\bmorning\b/i.test(s)&&!/meal|food/i.test(s)) {
    return {freq:"In the morning",max:null,timing:null};
  }
  if(/bedtime|nightly|\bhs\b|at night|before bed/i.test(s)) {
    if(/twice|two\s*times|bid/i.test(s)&&/meal|food/i.test(s)) return {freq:"With meals and at bedtime",max:null,timing:null};
    if(/twice|two\s*times|bid/i.test(s)) return {freq:"Twice daily and at bedtime",max:null,timing:null};
    if(/once.*day|one.*day|daily/i.test(s)) return {freq:"At bedtime",max:null,timing:null};
    return {freq:"At bedtime",max:null,timing:null};
  }
  if(/breakfast.*dinner|dinner.*breakfast/i.test(s)) return {freq:"With breakfast and dinner",max:null,timing:null};
  if(/every meal|with meal|with food/i.test(s)) return {freq:"With every meal",max:null,timing:null};
  if(/breakfast/i.test(s)) return {freq:"With breakfast",max:null,timing:null};
  if(/lunch/i.test(s)) return {freq:"With lunch",max:null,timing:null};
  if(/dinner|supper/i.test(s)) return {freq:"With dinner",max:null,timing:null};
  if(/every other day|alternate day/i.test(s)) return {freq:"Every other day",max:null,timing:null};
  if(/once.*month|monthly/i.test(s)) return {freq:"Once monthly",max:null,timing:null};
  if(/3.*week|three.*week/i.test(s)) return {freq:"Three times weekly",max:null,timing:null};
  if(/2.*week|twice.*week/i.test(s)) return {freq:"Twice weekly",max:null,timing:null};
  // Named day weekly patterns — "every Sunday", "every Monday AM", etc.
  const DAY_MAP = {sunday:"Sun",monday:"Mon",tuesday:"Tue",wednesday:"Wed",thursday:"Thu",friday:"Fri",saturday:"Sat"};
  // Multi-day patterns: Monday, Wednesday and Friday / Mon, Wed, Fri etc.
  // Only trigger if string contains explicit day names (not just "three times weekly")
  const DAY_ABBR = {sun:"Sun",sunday:"Sun",mon:"Mon",monday:"Mon",tue:"Tue",tuesday:"Tue",wed:"Wed",wednesday:"Wed",thu:"Thu",thursday:"Thu",fri:"Fri",friday:"Fri",sat:"Sat",saturday:"Sat"};
  const hasDayNames = /\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|wed|thu|fri|sat)\b/i.test(s);
  if(hasDayNames) {
    const multiDayMatches = [...s.matchAll(/\b(sun(?:day)?|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?)\b/gi)];
    if(multiDayMatches.length >= 2) {
      const scheduledDays = [...new Set(multiDayMatches.map(m => DAY_ABBR[m[1].toLowerCase()]).filter(Boolean))];
      if(scheduledDays.length === 2) return {freq:"Twice weekly", max:null, timing:null, scheduledDays};
      if(scheduledDays.length >= 3) return {freq:"Three times weekly", max:null, timing:null, scheduledDays};
    }
  }
  const namedDayMatch = s.match(/every\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i);
  if(namedDayMatch) return {freq:"Once weekly",max:null,timing:null,scheduledDay:DAY_MAP[namedDayMatch[1].toLowerCase()]};
  if(/once.*week|weekly|every\s*7\s*day/i.test(s)) return {freq:"Once weekly",max:null,timing:null};
  if(/every\s*2\s*hour/i.test(s)) return {freq:"Every 2 hours",max:null,timing:null};
  if(/every\s*4\s*hour/i.test(s)) return {freq:"Every 4 hours",max:null,timing:null};
  if(/every\s*6\s*hour/i.test(s)) return {freq:"Every 6 hours",max:null,timing:null};
  if(/every\s*8\s*hour/i.test(s)) return {freq:"Every 8 hours",max:null,timing:null};
  if(/every\s*12\s*hour/i.test(s)) return {freq:"Every 12 hours",max:null,timing:null};
  if(/every\s*24\s*hour/i.test(s)) return {freq:"Every 24 hours",max:null,timing:null};
  if(/every\s*48\s*hour/i.test(s)) return {freq:"Every 48 hours",max:null,timing:null};
  if(/6.*day|six.*day/i.test(s)) return {freq:"Six times daily",max:null,timing:null};
  if(/5.*day|five.*day/i.test(s)) return {freq:"Five times daily",max:null,timing:null};
  if(/4.*day|four.*day|\bqid\b/i.test(s)) return {freq:"Four times daily",max:null,timing:null};
  if(/3.*day|three.*day|\btid\b|3\s*times/i.test(s)) return {freq:"Three times daily",max:null,timing:null};
  if(/2.*day|twice|two.*day|\bbid\b|2\s*times/i.test(s)) return {freq:"Twice daily",max:null,timing:null};
  if(/once|daily|every day|each day/i.test(s)) return {freq:"Once daily",max:null,timing:null};
  return null;
}

function doseLabel(med, di, isAlternating = false) {
  // For alternating medications, add the medication name
  const medName = isAlternating ? (med.brand || med.name.split(" ")[0]) : null;
  
  if(isInterval(med.freq)) return med.freq;
  if(isPRN(med.freq)) {
    // Handle specific PRN frequencies
    if(med.freq === "As needed daily" && med.doses === 1) return isAlternating ? `As Needed\n${medName}\nDaily` : "As Needed Daily";
    if(med.freq === "As needed twice daily" && med.doses <= 2) return isAlternating ? `As Needed\n${medName}\nDose ${di+1}` : `As Needed Dose ${di+1}`;
    if(med.freq === "As needed at bedtime") return isAlternating ? `As Needed\n${medName}\nat Bedtime` : "As Needed at Bedtime";
    if(med.doses===1) {
      if(med.prnTiming==="bedtime") return isAlternating ? `As Needed\n${medName}\nat Bedtime` : "As Needed at Bedtime";
      if(med.prnTiming==="evening") return isAlternating ? `As Needed\n${medName}\nin the Evening` : "As Needed in the Evening";
      return isAlternating ? `As Needed\n${medName}\nDose 1` : "As Needed Dose 1";
    }
    return isAlternating ? `As Needed\n${medName}\nDose ${di+1}` : `As Needed Dose ${di+1}`;
  }
  if(med.doses===1) {
    if(isMORNING(med.freq)) return "Morning";
    if(isEVENING(med.freq)) return "Evening";
    if(isHS(med.freq)) return "Bedtime";
    if(med.freq === "Every 24 hours") return "Every 24 hrs";
    if(med.freq === "Every 48 hours") return "Every 48 hrs";
    if(med._stopMed) return "** STOP **";
    if(!med.freq || med.freq === OTHER_FREQ) return "As directed";
    return "Daily";
  }
  // Morning and evening explicit 2-dose
  if(med.freq === "In the morning and in the evening") {
    if(di === 0) return isAlternating ? `${medName}\nMorning` : "Morning";
    return isAlternating ? `${medName}\nEvening` : "Evening";
  }
  // Morning and bedtime explicit 2-dose
  if(med.freq === "In the morning and at bedtime") {
    if(di === 0) return isAlternating ? `${medName}\nMorning` : "Morning";
    return isAlternating ? `${medName}\nBedtime` : "Bedtime";
  }
  return isAlternating ? `${medName}\nDose ${di+1}` : `Dose ${di+1}`;
}

function prnScore(med) {
  const n = (med.notes||"").toLowerCase();
  const f = (med.freq||"").toLowerCase();
  
  // Primary: Group by reason (lower = higher priority)
  let reasonScore = 60; // default — unknown reason
  if(/mild|pain scale [1-3]|scale [1-3]/i.test(n)) reasonScore = 0;
  else if(/moderate|pain scale [4-7]|scale [4-7]/i.test(n)) reasonScore = 5;
  else if(/severe|pain scale [8-9]|pain scale 10|scale [8-9]|scale 10/i.test(n)) reasonScore = 10;
  else if(/\bpain\b/i.test(n)) reasonScore = 7; // unspecified pain — between moderate and severe
  else if(/nausea|vomit/i.test(n)) reasonScore = 20;
  else if(/anxiety/i.test(n)) reasonScore = 30;
  else if(/spasm|muscle/i.test(n)) reasonScore = 40;
  else if(/constipation|bowel/i.test(n)) reasonScore = 50;
  else if(/sleep/i.test(n) || /bedtime/i.test(f)) reasonScore = 80; // Sleep/bedtime LAST
  
  // Secondary: Sort by interval within each reason group
  let timingScore = 2; // Default: no specific timing
  if(/every\s*\d+\s*hour/i.test(f)) timingScore = 0;       // Interval first (every X hours)
  else if(/daily|once a day/i.test(f)) timingScore = 1;     // Daily
  else if(/bedtime|evening/i.test(f)) timingScore = 3;      // Bedtime last within group
  
  return reasonScore + timingScore;
}


function bucketMeds(meds, sortAlpha=false) {
  const all = meds.filter(m => m.name.trim());
  if(sortAlpha) {
    const sorted = [...all].sort((a,b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return [{label:"Medications (A–Z)", key:"alpha", meds:sorted}];
  }
  const prn = all.filter(m => isPRN(m.freq));
  const nonPRN = all.filter(m => !isPRN(m.freq));
  const day = nonPRN.filter(m => !isHSGroup(m.freq));
  const hs = nonPRN.filter(m => isHSGroup(m.freq));
  const sort = (arr) => {
    const timingOrder = (m) => {
      if(isMORNING(m.freq)) return 0;
      if(m.freq === "In the morning and in the evening") return 1;  // explicit morning+evening before generic multi-dose
      if(m.freq === "In the morning and at bedtime") return 1;      // explicit morning+bedtime before generic multi-dose
      if(m.doses === 1 && !isEVENING(m.freq) && !isHS(m.freq)
        && !/noon|midday|12\s*p|afternoon|lunch/i.test(m.freq)) return 2;
      if(/noon|midday|12\s*p|afternoon|lunch/i.test(m.freq)) return 3;
      if(m.doses > 1) return 4;
      if(isEVENING(m.freq)) return 5;
      if(isHS(m.freq)) return 6;
      return 2;
    };
    return [...arr].sort((a,b) => timingOrder(a) - timingOrder(b));
  };
  const hsEv = hs.filter(m => isEVENING(m.freq));
  const hsBd = hs.filter(m => !isEVENING(m.freq));
  const prnSorted = [...prn].sort((a,b) => prnScore(a)-prnScore(b));
  return [
    {label:"Daytime Medications", key:"day", meds:[...sort(day),...sort(hsEv),...sort(hsBd)]},
    {label:"As Needed Medications (PRN)", key:"prn", meds:prnSorted},
  ].filter(b => b.meds.length>0);
}

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Format name as "First Last-Initial" for HIPAA compliance on printed docs
const formatNameForPrint = (fullName) => {
  if (!fullName || !fullName.trim()) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]; // Just first name
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0].toUpperCase();
  return `${firstName} ${lastInitial}.`;
};

const fmtD = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

// Check if a medication is active on a given date
const isMedActive = (med, date) => {
  if (!med.dateStarted || !med.duration) return true;
  
  // Handle bare number ("7") or "7 days" format
  const durationStr = String(med.duration).trim();
  const durationDays = parseInt(durationStr);
  if (isNaN(durationDays) || durationDays <= 0) return true;
  
  // Parse start date from YYYY-MM-DD string
  const startParts = med.dateStarted.split('-').map(Number);
  const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  
  // Calculate end date
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + durationDays - 1);
  
  // Compare dates (set all to midnight for consistent comparison)
  const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  
  return checkDate >= start && checkDate <= end;
};
function makeDates(start, n) {
  const parts = start.split("-").map(Number);
  const s = new Date(parts[0], parts[1]-1, parts[2]);
  return Array.from({length:n}, (_,i) => { const dt=new Date(s); dt.setDate(s.getDate()+i); return dt; });
}

const FONT = {
  compact: {base:7,  med:8,  hdr:8,  ttl:11, cell:14},
  normal:  {base:10, med:11, hdr:11, ttl:14, cell:22},
  large:   {base:13, med:14, hdr:13, ttl:17, cell:28},
  xlarge:  {base:16, med:17, hdr:16, ttl:20, cell:34},
};

const newMed = () => ({id:Date.now()+Math.random(), name:"", brand:"", dose:"", qty:"", freq:"", doses:1, maxDailyDoses:null, prnTiming:null, notes:"", additionalNotes:"", customFreq:"", alternatesWith:null, _altChecked:false, _aiParsed:false, scheduledDays:[], duration:"", dateStarted:"", refillsLeft:"", prescriber:"", rxNumber:"", pillDescription:""});

const AI_SYSTEM = `You are a medication parser. Return ONLY valid JSON.

CRITICAL: For the freq field, return THE EXACT TEXT FROM THE BOTTLE as written - do NOT normalize, do NOT translate to standard terms. Return what you read verbatim.

Examples:
- Bottle says "in the morning at noon and in the evening" → freq: "in the morning at noon and in the evening"
- Bottle says "three times a day" → freq: "three times a day"  
- Bottle says "take twice daily" → freq: "take twice daily"
- Bottle says "TID" → freq: "TID"
- Bottle says "every 12 hours" → freq: "every 12 hours"

For maxDailyDoses:
- PRN/as needed once daily → maxDailyDoses:1
- PRN twice daily → maxDailyDoses:2  
- PRN 3 times daily → maxDailyDoses:3
- every 4 hours PRN → use freq from bottle, maxDailyDoses:6
- every 6 hours PRN → use freq from bottle, maxDailyDoses:4
- every 8 hours PRN → use freq from bottle, maxDailyDoses:3
- every 12 hours PRN → use freq from bottle, maxDailyDoses:2
- every 8 hours PRN → "Every 8 hours as needed"
- every 12 hours PRN → "Every 12 hours as needed"
- every 7 days → "Once weekly"
- maxDailyDoses: ONLY set for PRN/as-needed meds. ALWAYS null for scheduled frequencies.
- brand: if label says "Common brand(s):" or similar, extract the brand name.
For notes: include indication ONLY. EXCLUDE pharmacy, pickup, duration, refills, follow-up.
ASYMMETRIC DOSES: If the sig specifies different quantities at different times of day (e.g. "take 1 tablet in the morning and 2 tablets in the evening", "take 2 tablets every morning and 1 tablet at bedtime"), put BOTH quantities in the qty field separated by a slash (e.g. qty: "1 tablet morning / 2 tablets evening"). Do NOT just pick one quantity and discard the other.

ICD-10 CODES: If the sig contains ICD-10 diagnosis codes (patterns like J43.2, I10, E11.9, J432, or any letter followed by numbers with optional decimal) these are billing codes, NOT frequency. Strip them entirely from freq. If the code appears at the end of the sig after the frequency, ignore it. Never put an ICD-10 code in the freq field.

DOSING CONDITIONS: If the sig contains a conditional qualifier like "while on dexamethasone", "while taking X", "during chemotherapy", "while on steroid course", "until finished", "while symptomatic" — these are NOT frequency and NOT reason. Put them in additionalNotes (e.g. additionalNotes: "while on dexamethasone course"). The freq should only contain the dosing interval (e.g. "in the morning"), not the condition.

NUTRITIONAL PRODUCTS: If the label is for a nutritional formula (e.g. Osmolite, Ensure, Boost, Jevity, Glucerna, Kate Farms, or any product labeled "Complete Nutrition", "Balanced Nutrition", "Enteral Formula", or showing Cal/mL or Cal/oz): name should be the product brand name (e.g. "Osmolite", "Ensure Clear") — do NOT use "enteral formula" as the name. brand should be the same brand name. dose is the caloric density if shown (e.g. "1.5 Cal/mL", "240 calories"). qty is the serving size (e.g. "8 fl oz", "237 mL"). freq: if a specific frequency is stated in the document (e.g. "4 times a day", "every 4 hours"), extract it normally. If no frequency is stated on the label or package, leave freq as empty string since feeding frequency comes from the doctor's order. notes should be "complete balanced nutrition" or similar from the label. additionalNotes must be empty string — do NOT put anything in additionalNotes. Set a special field: _isNutrition: true.`;

const AI_PROMPT = "Return ONLY a JSON object: {name, brand, dose, qty, freq, maxDailyDoses, notes, additionalNotes, duration, refillsLeft, prescriber, rxNumber, pillDescription, patientName}. name: generic name only, no strength. brand: brand name or empty string. dose: the strength PRINTED ON THE TABLET OR PACKAGE (e.g. if label says '25mg tablet take 0.5 tablet', dose is '25mg' — do NOT calculate, do NOT convert). qty: how many units AND route (e.g. '0.5 tablet by mouth', '1 tablet by mouth', '4 sprays both nostrils') — never use mg as quantity. freq: RETURN THE EXACT TEXT FROM THE BOTTLE for frequency (e.g., 'in the morning at noon and in the evening', 'three times a day', 'take 1 tablet by mouth twice daily') — do NOT normalize to standard strings, just return what the label says. maxDailyDoses: integer if PRN with stated max else null. notes: indication/reason for taking (e.g., 'for diabetes', 'for high blood pressure') — no pharmacy, pickup, duration, refill, route, or quantity info. additionalNotes: any auxiliary warning sticker instructions visible on the bottle such as 'do not crush', 'swallow whole', 'take with food or milk', 'avoid grapefruit', 'may cause dizziness', 'do not chew' — combine multiple warnings into one string separated by commas. Empty string if none visible. duration: number of days if stated on label (e.g. '15 days', '10 days') else empty string. refillsLeft: number of refills remaining if shown on label, else empty string. prescriber: prescribing doctor's name if shown on label, else empty string. rxNumber: prescription/RX number if shown on label, else empty string. pillDescription: brief physical description of pill/tablet (e.g., 'white oval tablet', 'yellow capsule', 'round blue pill') if visible or stated, else empty string. patientName: patient's full name if shown on label (usually at top), else empty string.";


function medCell(med, fz) {
  const route = getRoute(med);
  const routeLabel = route !== "PO" ? route : "by mouth";
  const qtyHasRoute = med.qty && /mouth|nostril|spray|inhale|inject|eye|ear|skin|patch|rectum|sublingual/i.test(med.qty);
  const howMuch = med.qty
    ? (med._isNutrition || qtyHasRoute) ? med.qty : med.qty + " · " + routeLabel
    : med._isNutrition ? "" : routeLabel;
  const when = med.freq === OTHER_FREQ ? med.customFreq : med.freq;
  // Strip "As needed" from freq display — notes line carries that context
  const whenDisplay = isPRN(med.freq) 
    ? when.replace(/^as\s+needed\s*/i,"").replace(/\s+as\s+needed$/i,"").trim()
    : when;
  
  // Format start date if present
  const startDateFormatted = med.dateStarted ? (() => {
    const parts = med.dateStarted.split('-');
    if (parts.length === 3) {
      const mm = parts[1].padStart(2, '0');
      const dd = parts[2].padStart(2, '0');
      const yy = parts[0].slice(-2);
      return `${mm}/${dd}/${yy}`;
    }
    return null;
  })() : null;
  
  const brandSame = med.brand && med.brand.toLowerCase().trim() === med.name.toLowerCase().trim();
  const brandPart = (med.brand && !brandSame) ? ` (${med.brand})` : "";
  const dosePart = med.dose ? ` ${med.dose}` : "";
  
  // Build frequency line — PRN meds show stripped interval, scheduled show full freq + duration
  let freqLine = "";
  if (!isPRN(med.freq)) {
    // Strip "for X days" from freq since duration is handled separately
    const cleanWhen = whenDisplay.replace(/\s+for\s+\d+\s*days?\.?/i,"").trim();
    freqLine = cleanWhen;
    if (med.duration) freqLine += ` for ${med.duration}`;
  } else if (whenDisplay) {
    freqLine = whenDisplay;
  }
  
  return (
    <div>
      <div style={{fontWeight:"bold",fontSize:fz.med}}>{med.name}{brandPart}{dosePart && <span style={{fontWeight:"normal",color:"#555"}}> {med.dose}</span>}</div>
      {howMuch && freqLine && <div style={{fontSize:fz.base,color:"#444",paddingLeft:8}}>— {howMuch} · {freqLine}</div>}
      {howMuch && !freqLine && <div style={{fontSize:fz.base,color:"#444",paddingLeft:8}}>— {howMuch}</div>}
      {!howMuch && freqLine && <div style={{fontSize:fz.base,color:"#444",paddingLeft:8}}>— {freqLine}</div>}
      {med.notes && <div style={{fontSize:fz.base,fontStyle:"italic",color:"#666",paddingLeft:8}}>{isPRN(med.freq) ? `As needed for ${med.notes.replace(/^(for\s*:?\s*)/i,"").trim()}` : `Reason: ${med.notes.replace(/^(for\s*:?\s*)/i,"").trim()}`}</div>}
      {med.additionalNotes && <div style={{fontSize:fz.base,fontStyle:"italic",color:"#666",paddingLeft:8}}>{med.additionalNotes}</div>}
      {startDateFormatted && <div style={{fontSize:fz.base,color:"#2563eb",paddingLeft:8}}>— Started: {startDateFormatted}</div>}
    </div>
  );
}

function Sheet({patient, dates, meds, weekNum, fz, vitals={}, sortAlpha=false}) {
  const buckets = bucketMeds(meds, sortAlpha);
  const cols = 2 + dates.length;

  const renderMed = (med, stripe) => {
    const isAltTarget = meds.some(m => String(m.alternatesWith)===String(med.id));
    const altMed = !isAltTarget && med.alternatesWith ? meds.find(m => String(m.id)===String(med.alternatesWith)) : null;

    if(isInterval(med.freq)) {
      const scheduled = med.scheduledDays||[];
      const maxDays = med.freq==="Once weekly"?1:med.freq==="Twice weekly"?2:med.freq==="Three times weekly"?3:7;
      return (
        <tr key="iv" style={{background:stripe%2===0?"#f9f9f9":"#fff"}}>
          <td style={{border:"1px solid #bbb",padding:"3px 4px",verticalAlign:"top",lineHeight:1.4}}>{medCell(med,fz)}</td>
          <td style={{border:"1px solid #bbb",padding:"2px 3px",textAlign:"center",fontSize:fz.base,color:"#555",verticalAlign:"middle"}}>{med.freq}</td>
          {scheduled.length > 0 ? (
            dates.map((d,wi) => {
              const dayName = DAYS[d.getDay()];
              const isScheduled = scheduled.includes(dayName);
              const isActive = isMedActive(med, d);
              const shouldBlock = !isScheduled || !isActive;
              return (
                <td key={wi} style={{border:"1px solid #bbb",padding:0,textAlign:"center",verticalAlign:"middle",background:shouldBlock?"#f0f0f0":"#fff",height:fz.cell}}>
                  {shouldBlock && (
                    <div style={{position:"relative",width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg viewBox="0 0 100 100" style={{width:"60%",height:"60%",maxWidth:30,maxHeight:30}}>
                        <line x1="10" y1="10" x2="90" y2="90" stroke="#999" strokeWidth="6"/>
                        <line x1="90" y1="10" x2="10" y2="90" stroke="#999" strokeWidth="6"/>
                      </svg>
                    </div>
                  )}
                </td>
              );
            })
          ) : (
            <td colSpan={dates.length} style={{border:"1px solid #bbb",padding:"4px 8px",verticalAlign:"middle",fontSize:fz.base}}>
              <div style={{color:"#555"}}>Last dose: _______________________</div>
              <div style={{color:"#555",marginTop:3}}>Next due: _______________________</div>
            </td>
          )}
        </tr>
      );
    }

    if(altMed) {
      const medAName = med.brand || med.name.split(" ")[0];
      const medBName = altMed.brand || altMed.name.split(" ")[0];
      const totalDoses = Math.max(med.doses, altMed.doses);
      const rows = [];
      for(let di=0; di<totalDoses; di++) {
        rows.push(
          <tr key={"a"+di} style={{background:stripe%2===0?"#f9f9f9":"#fff"}}>
            {di===0 && (
              <td rowSpan={totalDoses*2} style={{border:"1px solid #bbb",padding:"3px 4px",verticalAlign:"top",lineHeight:1.4}}>
                {medCell(med,fz)}
                <div style={{fontSize:fz.base-1,fontStyle:"italic",color:"#a05000",margin:"4px 0 2px"}}>*** Alternate with ***</div>
                {medCell(altMed,fz)}
              </td>
            )}
            <td style={{border:"1px solid #bbb",padding:"2px 3px",textAlign:"center",fontSize:fz.base,color:"#a05000",verticalAlign:"middle",background:"#fff8f2"}}>{medAName} Dose {di+1}</td>
            {dates.map((_,wi) => <td key={wi} style={{border:"1px solid #bbb",height:fz.cell}}/>)}
          </tr>
        );
        rows.push(
          <tr key={"b"+di} style={{background:"#fff0e6"}}>
            <td style={{border:"1px solid #bbb",padding:"2px 3px",textAlign:"center",fontSize:fz.base,color:"#7a3000",verticalAlign:"middle"}}>{medBName} Dose {di+1}</td>
            {dates.map((_,wi) => <td key={wi} style={{border:"1px solid #bbb",height:fz.cell}}/>)}
          </tr>
        );
      }
      return rows;
    }

    return Array.from({length:med.doses}).map((_,di) => (
      <tr key={di} style={{background:stripe%2===0?"#f9f9f9":"#fff"}}>
        {di===0 && (
          <td rowSpan={med.doses} style={{border:"1px solid #bbb",padding:"3px 4px",verticalAlign:"top",lineHeight:1.4}}>{medCell(med,fz)}</td>
        )}
        <td style={{border:"1px solid #bbb",padding:"2px 3px",textAlign:"center",fontSize:fz.base,color:isPRN(med.freq)?"#a05000":"#555",verticalAlign:"middle",wordBreak:"break-word",lineHeight:1.2}}>
          {doseLabel(med,di)}
        </td>
        {dates.map((d,wi) => {
          const isActive = isMedActive(med, d);
          return (
            <td key={wi} style={{border:"1px solid #bbb",height:fz.cell,padding:0,textAlign:"center",verticalAlign:"middle",background:isActive?"#fff":"#f0f0f0"}}>
              {!isActive && (
                <div style={{position:"relative",width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <svg viewBox="0 0 100 100" style={{width:"60%",height:"60%",maxWidth:30,maxHeight:30}}>
                    <line x1="10" y1="10" x2="90" y2="90" stroke="#999" strokeWidth="6"/>
                    <line x1="90" y1="10" x2="10" y2="90" stroke="#999" strokeWidth="6"/>
                  </svg>
                </div>
              )}
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <div className="week-sheet" style={{fontFamily:"Arial,sans-serif",fontSize:fz.base,padding:"10px 8px"}}>
      <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",borderBottom:"2px solid #000",paddingBottom:4,marginBottom:6}}>
        <div>
          <div style={{fontSize:fz.ttl,fontWeight:"bold"}}>Medication Log</div>
          <div style={{fontSize:fz.med}}>Name: <strong>{patient||"___________________________"}</strong></div>
        </div>
        <div style={{textAlign:"right",fontSize:fz.base}}>
          <div>Week {weekNum} | {fmtD(dates[0])} – {fmtD(dates[dates.length-1])}</div>
          <div style={{color:"#666"}}>Record time taken in each cell</div>
        </div>
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
        <colgroup>
          <col style={{width:"23%"}}/><col style={{width:"8%"}}/>
          {dates.map((_,i) => <col key={i} style={{width:(69/dates.length)+"%"}}/>)}
        </colgroup>
        <thead>
          <tr style={{background:"#222",color:"#fff"}}>
            <th style={{border:"1px solid #000",padding:"3px 4px",textAlign:"left",fontSize:fz.hdr}}>Medication / Dose / Frequency</th>
            <th style={{border:"1px solid #000",padding:"3px 4px",textAlign:"center",fontSize:fz.hdr}}>Dose #</th>
            {dates.map((d,i) => (
              <th key={i} style={{border:"1px solid #000",padding:"2px",textAlign:"center",fontSize:fz.hdr}}>
                <div style={{fontWeight:"bold"}}>{DAYS[d.getDay()]}</div>
                <div style={{fontWeight:"normal"}}>{fmtD(d)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(vitals.weight || vitals.bp || vitals.glucose) && [
            <tr key="vitals-hdr">
              <td colSpan={cols} style={{background:"#1a5fa0",color:"#fff",fontSize:fz.base,fontWeight:"bold",padding:"3px 6px",letterSpacing:"0.5px"}}>Daily Vitals</td>
            </tr>,
            ...[
              vitals.weight  && {label:"Weight",       unit:"lbs / kg"},
              vitals.bp      && {label:"Blood Pressure",unit:"mmHg"},
              vitals.glucose && {label:"Blood Glucose", unit:"mg/dL"},
              vitals.bm      && {label:"Bowel Movement", unit:"Y / N"},
            ].filter(Boolean).map((v,vi) => (
              <tr key={"v"+vi} style={{background:vi%2===0?"#f0f6ff":"#fff"}}>
                <td style={{border:"1px solid #bbb",padding:"3px 4px",verticalAlign:"middle",fontSize:fz.med,fontWeight:600}}>{v.label}<div style={{fontSize:fz.base-1,color:"#888",fontWeight:400}}>{v.unit}</div></td>
                <td style={{border:"1px solid #bbb",padding:"2px 3px",textAlign:"center",fontSize:fz.base,color:"#555",verticalAlign:"middle"}}>Daily</td>
                {dates.map((_,wi) => <td key={wi} style={{border:"1px solid #bbb",height:fz.cell+4}}/>)}
              </tr>
            ))
          ]}
          {buckets.map((bucket,bi) => [
            <tr key={"d"+bi}>
              <td colSpan={cols} style={{background:"#333",color:"#fff",fontSize:fz.base,fontWeight:"bold",padding:"3px 6px",letterSpacing:"0.5px"}}>{bucket.label}</td>
            </tr>,
            ...bucket.meds.filter(med => !meds.some(m => String(m.alternatesWith)===String(med.id))).map((med,mi) => renderMed(med,mi)),
          ])}
        </tbody>
      </table>
      <div style={{marginTop:6,borderTop:"1px solid #ccc",paddingTop:4}}>
        <div style={{fontSize:fz.base-1,color:"#555",marginBottom:2}}>Write time taken in each cell.</div>
        <div style={{fontSize:fz.base-2,color:"#777",fontStyle:"italic",lineHeight:1.5}}>
          <strong style={{fontStyle:"normal"}}>DISCLAIMER:</strong> This record is for personal and caregiver tracking purposes only. It is not a substitute for professional medical advice, diagnosis, or treatment. Always consult your doctor or pharmacist before making any changes to medications. Reprint and update this record whenever medications are added, changed, or discontinued.
        </div>
        <div style={{fontSize:fz.base-2,color:"#999",marginTop:3,textAlign:"right"}}>© 2026 PillPaper™ — pillpaper.com. All rights reserved.</div>
      </div>
      </div>{/* end inner scaling div */}
    </div>
  );
}

export default function App() {
  const windowWidth = useWindowWidth();
  const isMobileLayout = windowWidth <= 640;
  const [patient,   setPatient]   = useState("");
  const [start,     setStart]     = useState(() => new Date().toISOString().slice(0,10));
  const [meds,      setMeds]      = useState([newMed()]);
  const [fzKey,     setFzKey]     = useState("normal");
  const [vitals,    setVitals]    = useState({weight:false, bp:false, glucose:null, bm:false});
  const [aiIdx,     setAiIdx]     = useState(null);
  const [bulk,      setBulk]      = useState("");
  const [bulkImg,   setBulkImg]   = useState(false);
  const [bulkMode,  setBulkMode]  = useState("text"); // "text" | "bottle" | "list"
  const [bulkPastedImg, setBulkPastedImg] = useState(null); // pasted screenshot File
  const [showBulkListModal, setShowBulkListModal] = useState(false); // photo list modal
  const [showMedListModal, setShowMedListModal] = useState(false); // med list options modal
  const [showMedListText, setShowMedListText] = useState(false); // text view modal
  const [medListFzKey, setMedListFzKey] = useState("normal"); // size for med list PDF
  const [medListCopied, setMedListCopied] = useState(false); // copy confirmation
  const [bulkBusy,  setBulkBusy]  = useState(false);
  const [bulkMsg,   setBulkMsg]   = useState("");
  const [bulkFile1, setBulkFile1] = useState(null);
  const [bulkFile2, setBulkFile2] = useState(null);
  const [saveMsg,   setSaveMsg]   = useState("");
  const [confirm,   setConfirm]   = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({}); // per-med confirm state
  const [removing, setRemoving] = useState({}); // per-med removing state
  const [removeMsg, setRemoveMsg] = useState(""); // toast after removal
  const [showLog,   setShowLog]   = useState(false);
  const [cLog,      setCLog]      = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [fbForm,    setFbForm]    = useState({what:"", wrong:"", device:"", email:""});
  const [fbStatus,  setFbStatus]  = useState("idle");
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms,   setShowTerms]   = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(() => {
    try { return !localStorage.getItem("pillpaper_beta_seen"); } catch(e) { return false; }
  }); // idle | sending | sent | error
  const [showPrint, setShowPrint] = useState(false);
  const [printWeeks,setPrintWeeks]= useState(4);
  const [printDays, setPrintDays] = useState(null); // null = use weeks, number = exact days
  const [pdfBusy,   setPdfBusy]  = useState(false);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [showAllPreview, setShowAllPreview] = useState(false);
  const fileRefs   = useRef({});
  const fileRefs2  = useRef({});
  const bulkImgRef = useRef(null);
  const bulkImgRef2 = useRef(null);
  const bulkTextareaRef = useRef(null);
  const [pendingPhotos, setPendingPhotos] = useState({});
  const [pendingPhotoModal, setPendingPhotoModal] = useState(null); // {medIndex, medName}
  const [testimonials, setTestimonials] = useState([]);
  const [showShareFeedback, setShowShareFeedback] = useState(false);
  const [shareFeedbackForm, setShareFeedbackForm] = useState({feedback: "", name: "", role: ""});
  const [shareFeedbackStatus, setShareFeedbackStatus] = useState("idle"); // idle, sending, sent
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialPaused, setTestimonialPaused] = useState(false);
  const [aiScansUsed, setAiScansUsed] = useState(0);
  const AI_SCAN_LIMIT = 50; // 50 scans per session
  const [expandedWhyCards, setExpandedWhyCards] = useState({});
  const [sortAlpha, setSortAlpha] = useState(false);
  const [selectedWeeks, setSelectedWeeks] = useState(1);
  const [showDelivery, setShowDelivery] = useState(false);
  const [deliveryPdf, setDeliveryPdf] = useState(null); // {blob, fileName}
  const [approvedNameAliases, setApprovedNameAliases] = useState([]); // approved name pairs this session
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await lsGet("marfill_v1");
        if(r) {
          const d = JSON.parse(r.value);
          if(d.patient) setPatient(d.patient);
          if(d.start)   setStart(d.start);
          if(d.meds?.length) setMeds(d.meds);
          if(d.fzKey)   setFzKey(d.fzKey);
          if(d.vitals)  setVitals({weight:false,bp:false,glucose:null,bm:false,...d.vitals});
          if(d.sortAlpha !== undefined) setSortAlpha(d.sortAlpha);
        }
      } catch(e) {}
    })();
    
    // Load testimonials
    (async () => {
      try {
        const response = await fetch('/testimonials.json');
        if (response.ok) {
          const data = await response.json();
          setTestimonials(data);
        }
      } catch(e) {
        console.log('Could not load testimonials');
      }
    })();
  }, []);
  
  // Rotate testimonials every 5 seconds
  useEffect(() => {
    if (testimonials.length === 0 || testimonialPaused) return;
    const timer = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length, testimonialPaused]);

  // SEO Meta Tags
  useEffect(() => {
    // Set page title
    document.title = "PillPaper™ - Free Printable Medication Tracker | Never Miss a Dose";
    
    // Create or update meta tags
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    setMetaTag('description', 'Free printable medication tracker for families, caregivers, and pet owners. Simple paper MAR log - no app, no login, no data stored. Track pills, antibiotics, and treatments.');
    setMetaTag('keywords', 'medication tracker, pill tracker, medication log, MAR, medication administration record, printable medication chart, pill organizer, medication schedule, caregiver tools, pet medication tracker');
    setMetaTag('author', 'PillPaper');
    
    // Open Graph tags (for social media sharing)
    setMetaTag('og:title', 'PillPaper™ - Free Printable Medication Tracker', true);
    setMetaTag('og:description', 'Never wonder if a dose was already given. Simple printable medication tracker for families, caregivers, and pets. No app, no login, free forever.', true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', 'https://pillpaper.com', true);
    setMetaTag('og:site_name', 'PillPaper', true);
    
    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', 'PillPaper™ - Free Printable Medication Tracker');
    setMetaTag('twitter:description', 'Simple printable medication tracker for families, caregivers, and pets. No app, no login, free forever.');
    
    // Additional SEO tags
    setMetaTag('robots', 'index, follow');
    setMetaTag('viewport', 'width=device-width, initial-scale=1.0');
  }, []);

  const save = async () => {
    try { await lsSet("marfill_v1", JSON.stringify({patient,start,meds,fzKey,vitals,sortAlpha})); setSaveMsg("Saved!"); } catch(e) { setSaveMsg("Save failed."); }
    setTimeout(() => setSaveMsg(""), 3000);
  };

  const clear = async () => {
    if(!confirm) { setConfirm(true); setTimeout(() => setConfirm(false), 4000); return; }
    try { await lsDel("marfill_v1"); } catch(e) {}
    setPatient(""); setStart(new Date().toISOString().slice(0,10));
    setMeds([newMed()]); setFzKey("normal"); setVitals({weight:false,bp:false,glucose:null,bm:false});
    setBulk(""); setBulkFile1(null); setBulkFile2(null); setBulkImg(false); setBulkMsg(""); setBulkPastedImg(null); setBulkMode("text"); setShowBulkListModal(false);
    setConfirm(false); setSaveMsg("Cleared."); setTimeout(() => setSaveMsg(""), 3000);
  };

  const isMobile = () => /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);

  const checkLandscapeWarning = (file, onProceed) => {
    if(!isMobile()) { onProceed(); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if(img.width > img.height) {
        alert("📱 This photo looks sideways.\n\nPlease hold your phone upright (portrait) and retake the photo.");
      } else {
        onProceed();
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); onProceed(); };
    img.src = url;
  };

  // ── MED LIST GENERATION ──
  const buildMedListText = () => {
    const lines = [];
    const printName = formatNameForPrint(patient);
    if(printName) lines.push(`MEDICATION LIST — ${printName}`);
    else lines.push("MEDICATION LIST");
    lines.push(`Generated ${new Date().toLocaleDateString('en-US')}`);
    lines.push("");
    const buckets = bucketMeds(namedM, sortAlpha);
    let num = 1;
    buckets.forEach(bucket => {
      if(bucket.key === "prn") lines.push("--- AS NEEDED (PRN) ---");
      bucket.meds.filter(m => !namedM.some(x => String(x.alternatesWith)===String(m.id))).forEach(med => {
        const brandPart = (med.brand && med.brand.toLowerCase() !== med.name.toLowerCase()) ? ` (${med.brand})` : "";
        const freq = med.freq === "Other — type your own" ? med.customFreq : med.freq;
        lines.push(`${num}. ${med._stopMed ? "** STOP — DO NOT TAKE ** " : ""}${med.name}${brandPart}${med.dose ? " " + med.dose : ""}`);
        if(med._stopMed) lines.push(`   *** This medication was listed as DISCONTINUED — remove or verify with prescriber ***`);
        if(med.qty || freq) lines.push(`   Dose: ${[med.qty, freq].filter(Boolean).join(" · ")}`);
        if(med.duration && !med._stopMed) lines.push(`   Course: ${med.duration}`);
        if(med.notes) lines.push(`   Reason: ${med.notes}`);
        if(med.additionalNotes) lines.push(`   Notes: ${med.additionalNotes}`);
        lines.push("");
        num++;
      });
    });
    // Note: STOP meds are included with warning
    lines.push("Generated by PillPaper™ — pillpaper.com");
    lines.push("");
    lines.push("DISCLAIMER: For personal tracking only. Not a substitute for medical advice.");
    lines.push("Always verify with your pharmacist or prescriber.");
    return lines.join("\n");
  };

  const generateMedListPDF = async (fzKeyOverride="normal") => {
    const fzMap = {compact:{base:7,title:10}, normal:{base:10,title:14}, large:{base:13,title:17}, xlarge:{base:16,title:20}};
    const fzSizes = fzMap[fzKeyOverride] || fzMap.normal;
    const pdf = new jsPDF({ orientation:"portrait", unit:"pt", format:"letter" });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 36;
    const fzBase = fzSizes.base;
    const fzTitle = fzSizes.title;

    // Header
    pdf.setFont("helvetica","bold");
    pdf.setFontSize(fzTitle);
    const printName = formatNameForPrint(patient);
    pdf.text(printName ? `Medication List — ${printName}` : "Medication List", margin, margin + 10);
    pdf.setFont("helvetica","normal");
    pdf.setFontSize(fzBase);
    pdf.setTextColor(100);
    pdf.text(`Generated ${new Date().toLocaleDateString('en-US')} · pillpaper.com`, margin, margin + 20);
    pdf.setTextColor(0);

    const buckets = bucketMeds(namedM, sortAlpha);
    const body = [];
    buckets.forEach(bucket => {
      if(bucket.key === "prn") {
        body.push([{content:"As Needed Medications (PRN)", colSpan:3, styles:{fillColor:[200,200,200],fontStyle:"bold",fontSize:fzBase}}]);
      }
      bucket.meds.filter(m => !namedM.some(x => String(x.alternatesWith)===String(m.id))).forEach(med => {
        const brandPart = (med.brand && med.brand.toLowerCase() !== med.name.toLowerCase()) ? ` (${med.brand})` : "";
        const freq = med.freq === "Other — type your own" ? med.customFreq : med.freq;
        const nameStr = `${med.name}${brandPart}${med.dose ? " " + med.dose : ""}`;
        const doseStr = [med.qty, freq].filter(Boolean).join(" · ");
        const reasonStr = [med.notes, med.additionalNotes].filter(Boolean).join(" · ");
        const isStop = med._stopMed || false;
        const durationStr2 = (med.duration && !isStop) ? `\n${med.duration} course` : "";
        body.push([
          {content: (isStop ? "** STOP ** " : "") + nameStr, styles:{fontStyle:"bold", fontSize:fzBase, textColor:isStop?[220,38,38]:[0,0,0]}},
          {content: isStop ? "DISCONTINUED" : doseStr + durationStr2, styles:{fontSize:fzBase, textColor:isStop?[220,38,38]:[0,0,0]}},
          {content: isStop ? "Do not take — verify with prescriber" : reasonStr, styles:{fontSize:fzBase, textColor:isStop?[220,38,38]:[80,80,80]}},
        ]);
      });
    });

    autoTable(pdf, {
      startY: margin + 28,
      head: [[
        {content:"Medication", styles:{fontStyle:"bold"}},
        {content:"Dose / Frequency", styles:{fontStyle:"bold"}},
        {content:"Reason", styles:{fontStyle:"bold"}},
      ]],
      body,
      margin: {left:margin, right:margin},
      styles: {fontSize:fzBase, cellPadding:fzBase < 9 ? 2 : 4, overflow:"linebreak", textColor:[0,0,0]},
      headStyles: {fillColor:[30,58,95], textColor:[255,255,255], fontSize:fzBase, fontStyle:"bold"},
      columnStyles: {
        0: {cellWidth:(pageW-margin*2)*0.38},
        1: {cellWidth:(pageW-margin*2)*0.35},
        2: {cellWidth:(pageW-margin*2)*0.27},
      },
      didDrawPage: () => {
        const pageH = pdf.internal.pageSize.getHeight();
        pdf.setDrawColor(200,200,200);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageH-margin-22, pageW-margin, pageH-margin-22);
        pdf.setFontSize(fzBase-1);
        pdf.setTextColor(100);
        pdf.text("PillPaper™ — pillpaper.com", margin, pageH-margin-12);
        pdf.setFontSize(Math.max(fzBase-3, 5));
        pdf.text("For personal tracking only. Not a substitute for medical advice. Always verify with your pharmacist or prescriber.", margin, pageH-margin-4);
        pdf.setTextColor(0);
      }
    });

    const fileName = (patient?.trim() ? formatNameForPrint(patient)+"_" : "") + "MedList_" + fzKeyOverride + ".pdf";
    const blob = pdf.output("blob");
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if(isMobileDevice) {
      setDeliveryPdf({blob, fileName});
      setShowDelivery(true);
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href=url; a.download=fileName; a.click();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }
  };

  const doAction = (w) => {
    setShowWeekPicker(false);
    setPdfBusy(true);
    setPrintWeeks(w);
    setPrintDays(null);
    setShowPrint(true);
    setTimeout(() => generatePDF(w, null), 100);
  };

  const doActionCompact2Week = (w=2) => {
    setShowPrintConfirm(false);
    setSelectedWeeks(w);
    setPdfBusy(true);
    setPrintWeeks(w);
    setPrintDays(null);
    setShowPrint(true);
    // Temporarily use compact font for 2-week-per-page layout
    setTimeout(() => generatePDF(w, null, true), 100);
  };

  const generatePDF = async (w, days=null, forceCompact=false) => {
    setPdfBusy(true);
    try {
      const pdf = new jsPDF({ orientation:"landscape", unit:"pt", format:"letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const fz = FONT[forceCompact ? "compact" : fzKey];

      // Pre-load Phil image as base64
      let philBase64 = null;
      try {
        philBase64 = await new Promise((res) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.width; c.height = img.height;
            c.getContext("2d").drawImage(img, 0, 0);
            res(c.toDataURL("image/png"));
          };
          img.onerror = () => res(null);
          img.src = "/phil.png";
        });
      } catch(e) {}

      // Build date chunks for PDF — if days specified, first chunk gets all days up to 7, remainder on next page
      const allPdfDates = makeDates(start, days || w * 7);
      const pdfDates = days ? allPdfDates.slice(0, days) : allPdfDates;
      const pdfWeeks = [];
      // forceCompact = 2 weeks per page in compact layout
      if(forceCompact) {
        for(let i = 0; i < pdfDates.length; i += 14) pdfWeeks.push(pdfDates.slice(i, i+14));
      } else {
        for(let i = 0; i < pdfDates.length; i += 7) pdfWeeks.push(pdfDates.slice(i, i+7));
      }
      const weeksToRender = pdfWeeks;

      weeksToRender.forEach((dates, wi) => {
        if(wi > 0) pdf.addPage("letter", "landscape");
        
        // Track starting page for this week
        const weekStartPage = pdf.internal.getCurrentPageInfo().pageNumber;

        // ── Compact Header - 2 rows ──
        pdf.setFont("helvetica","bold");
        pdf.setFontSize(fz.ttl);
        const printName = formatNameForPrint(patient);
        const titleText = printName ? `Medication Log for ${printName}` : "Medication Log";
        pdf.text(titleText, margin, margin + 8);
        
        // Row 1: Centered instruction
        pdf.setFont("helvetica", fzKey === "xlarge" ? "bold" : "italic");
        pdf.setFontSize(7);
        pdf.setTextColor(80, 80, 80);
        pdf.text("Write time dose was taken each day in each cell", pageW / 2, margin + 8, {align:"center"});
        
        // Row 1 (right): Disclaimer
        pdf.setFont("helvetica", fzKey === "xlarge" ? "bold" : "normal");
        pdf.setFontSize(6);
        pdf.setTextColor(139, 0, 0);
        pdf.text("Always verify with pharmacist/prescriber", pageW - margin, margin + 8, {align:"right"});
        
        pdf.setFont("helvetica","normal");
        pdf.setTextColor(0);

        const headerY = margin + 12;

        // ── Build table data ──
        const dayHeaders = dates.map(d => `${fmtD(d)}\n${DAYS[d.getDay()]}`);
        const head = [["Medication / Dose / Frequency", "Dose #", ...dayHeaders]];

        const buckets = bucketMeds(namedM, sortAlpha);
        const body = [];

        // Vitals rows
        if(vitals.weight || vitals.bp || vitals.glucose || vitals.bm) {
          if(vitals.weight)  body.push([{content:"Daily Weight", styles:{fontSize:fz.base-1}}, {content:"lbs/kg", styles:{halign:"center",fontSize:fz.base-1}}, ...dates.map(()=>({content:""}))]);
          if(vitals.bm)      body.push([{content:"Bowel Movement", styles:{fontSize:fz.base-1}}, {content:"Y / N", styles:{halign:"center",fontSize:fz.base-1}}, ...dates.map(()=>({content:""}))]);
          if(vitals.bp)      body.push([{content:"Blood Pressure", styles:{fontSize:fz.base-1}}, {content:"mmHg", styles:{halign:"center",fontSize:fz.base-1}}, ...dates.map(()=>({content:""}))]);
          if(vitals.glucose) {
            // Determine how many rows based on frequency
            const glucoseDoses = vitals.glucose === "Three times daily" ? 3 : vitals.glucose === "Twice daily" ? 2 : vitals.glucose === "Four times daily" ? 4 : 1;
            if (glucoseDoses === 1) {
              body.push([{content:"Blood Glucose", styles:{fontSize:fz.base-1}}, {content:"mg/dL", styles:{halign:"center",fontSize:fz.base-1}}, ...dates.map(()=>({content:""}))]);
            } else {
              for (let i = 0; i < glucoseDoses; i++) {
                body.push([
                  i === 0 ? {content:"Blood Glucose", rowSpan: glucoseDoses, styles:{fontSize:fz.base-1}} : null,
                  {content:`Reading ${i+1}`, styles:{halign:"center",fontSize:fz.base-1}},
                  ...dates.map(()=>({content:""}))
                ].filter(Boolean));
              }
            }
          }
        }

        buckets.forEach(bucket => {
          // Section header row
          // Only show section header for PRN — skip "Daytime Medications"
          if(bucket.key === "prn") {
            body.push([{content: bucket.label, colSpan: 2 + dates.length, styles:{fillColor:[200,200,200],textColor:[0,0,0],fontStyle:"bold"}}]);
          }

          bucket.meds
            .filter(med => !namedM.some(m => String(m.alternatesWith)===String(med.id)))
            .forEach(med => {
              const altMed = med.alternatesWith ? namedM.find(m => String(m.id)===String(med.alternatesWith)) : null;
              
              // Helper to build medication details
              const buildMedDetails = (m) => {
                const route = getRoute(m);
                const routeLabel = route !== "PO" ? route : "by mouth";
                const qtyHasRoute = m.qty && /mouth|nostril|spray|inhale|inject|eye|ear|skin|patch|rectum|sublingual/i.test(m.qty);
                const howMuch = m.qty ? ((m._isNutrition || qtyHasRoute) ? m.qty : m.qty + " · " + routeLabel) : (m._isNutrition ? "" : routeLabel);
                const when = (m.customFreq ? m.customFreq : (m.freq === OTHER_FREQ ? m.customFreq : m.freq)).replace(/\s+for\s+\d+\s*days?/gi,"").trim();
                const prnMed = isPRN(m.freq);
                // Strip "As needed" from freq display — notes line carries that context
                const whenDisplay = prnMed 
                  ? when.replace(/^as\s+needed\s*/i,"").replace(/\s+as\s+needed$/i,"").trim()
                  : when;
                
                // Format start date if present
                const startDateFormatted = m.dateStarted ? (() => {
                  const parts = m.dateStarted.split('-');
                  if (parts.length === 3) {
                    const mm = parts[1].padStart(2, '0');
                    const dd = parts[2].padStart(2, '0');
                    const yy = parts[0].slice(-2);
                    return `${mm}/${dd}/${yy}`;
                  }
                  return null;
                })() : null;
                
                // Name line includes strength inline
                const brandSame = m.brand && m.brand.toLowerCase().trim() === m.name.toLowerCase().trim();
                const brandPart = (m.brand && !brandSame) ? ` (${m.brand})` : "";
                const dosePart = m.dose ? ` ${m.dose}` : "";
                const line1 = `${m.name}${brandPart}${dosePart}`;
                
                // Duration shown separately as line4b — keep freq clean
                const whenWithDuration = when;
                
                const line2 = howMuch
                  ? (prnMed ? `  — ${howMuch}` : `  — ${howMuch} · ${whenWithDuration}`)
                  : (prnMed ? "" : `  — ${whenWithDuration}`);
                // line2b: for PRN show the interval (stripped of "As needed"); for scheduled it's already in line2
                const line2b = (prnMed && whenDisplay) ? `  — ${whenDisplay}` : "";
                const line3 = m.notes
                  ? `  ${prnMed ? "As needed for " : "Reason: "}${m.notes.replace(/^(for\s*:?\s*)/i,"").trim()}`
                  : "";
                const line3b = m.additionalNotes ? `  ${m.additionalNotes}` : "";
                const line4 = startDateFormatted ? `  — Started: ${startDateFormatted}` : "";
                const line4b = (m.duration && !m.dateStarted) ? `  Duration: ${m.duration.replace(/\s*days?/i,"").trim()} days` : "";
                
                // Bottle info line
                const bottleInfoParts = [];
                if(m.pillDescription) bottleInfoParts.push(`Pill: ${m.pillDescription}`);
                if(m.prescriber) bottleInfoParts.push(`Dr: ${m.prescriber}`);
                if(m.rxNumber) bottleInfoParts.push(`RX# ${m.rxNumber}`);
                if(m.refillsLeft) bottleInfoParts.push(`${m.refillsLeft} refills`);
                const bottleInfoLine = bottleInfoParts.length > 0 ? `  — ${bottleInfoParts.join(" · ")}` : "";
                
                return [line1, line2, line2b, line3, line3b, line4, line4b, bottleInfoLine].filter(Boolean).join("\n");
              };
              
              // Build cell text
              let cellText;
              if (altMed) {
                // Show both medications with separator
                const med1Details = buildMedDetails(med);
                const med2Details = buildMedDetails(altMed);
                cellText = `${med1Details}\n\n*** Alternate with ***\n\n${med2Details}`;
              } else {
                cellText = buildMedDetails(med);
              }
              if(isInterval(med.freq)) {
                const scheduled = med.scheduledDays||[];
                if(scheduled.length > 0) {
                  // Show individual day cells with X on non-scheduled days OR inactive days
                  body.push([
                    {content: cellText, styles:{fontSize:fz.base-1, valign:"top", textColor:[0,0,0]}},
                    {content: med.freq, styles:{halign:"center",fontSize:fz.base-1,valign:"middle"}},
                    ...dates.map(d => {
                      const isScheduled = scheduled.includes(DAYS[d.getDay()]);
                      const isActive = isMedActive(med, d);
                      const shouldBlock = !isScheduled || !isActive;
                      return {content: shouldBlock ? "✕" : "", styles:{halign:"center",textColor:[150,150,150],fillColor:shouldBlock?[240,240,240]:[255,255,255]}};
                    })
                  ]);
                } else {
                  // Show merged cell with "Last dose / Next due"
                  body.push([
                    {content: cellText, styles:{fontSize:fz.base-1, valign:"top", textColor:[0,0,0]}},
                    {content: med.freq, styles:{halign:"center",fontSize:fz.base-1,valign:"middle"}},
                    {content: "Last dose: _______________________\nNext due:  _______________________", colSpan: dates.length, styles:{fontSize:fz.base-1,textColor:[80,80,80],valign:"middle"}}
                  ]);
                }
                
                // Add separator after interval meds
                body.push([
                  {content: "", colSpan: 2 + dates.length, styles:{fillColor:[240,240,240], minCellHeight:1.5, cellPadding:0}}
                ]);
              } else {
                const altMed = med.alternatesWith ? namedM.find(m => String(m.id)===String(med.alternatesWith)) : null;
                
                if (altMed) {
                  // For alternating meds, show ALL doses for BOTH medications separately
                  const med1Doses = med.doses || 1;
                  const med2Doses = altMed.doses || 1;
                  const totalRows = med1Doses + med2Doses;
                  
                  // First row with cellText spanning all rows
                  body.push([
                    {content: cellText, rowSpan: totalRows, styles:{fontSize:fz.base-1, valign:"top", textColor:[0,0,0]}},
                    {content: doseLabel(med, 0, true), styles:{halign:"center", fontSize:fz.base-1, textColor: isPRN(med.freq)?[160,80,0]:[30,30,30]}},
                    ...dates.map(d => {
                      const isActive = isMedActive(med, d);
                      return {content: isActive ? "" : "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[150,150,150], fillColor:isActive?[255,255,255]:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                    })
                  ]);
                  
                  // Remaining doses for first medication
                  for(let di = 1; di < med1Doses; di++) {
                    body.push([
                      {content: doseLabel(med, di, true), styles:{halign:"center", fontSize:fz.base-1, textColor: isPRN(med.freq)?[160,80,0]:[30,30,30]}},
                      ...dates.map(d => {
                        const isActive = isMedActive(med, d);
                        return {content: isActive ? "" : "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[150,150,150], fillColor:isActive?[255,255,255]:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                      })
                    ]);
                  }
                  
                  // All doses for second medication
                  for(let di = 0; di < med2Doses; di++) {
                    body.push([
                      {content: doseLabel(altMed, di, true), styles:{halign:"center", fontSize:fz.base-1, textColor: isPRN(altMed.freq)?[160,80,0]:[30,30,30]}},
                      ...dates.map(d => {
                        const isActive = isMedActive(altMed, d);
                        return {content: isActive ? "" : "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[150,150,150], fillColor:isActive?[255,255,255]:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                      })
                    ]);
                  }
                } else {
                  // Non-alternating medication - original logic
                  const totalDoses = med.doses || 1;
                  body.push([
                    {content: cellText, rowSpan: totalDoses, styles:{fontSize:fz.base-1, valign:"top", textColor:[0,0,0]}},
                    {content: med._stopMed ? "** STOP **" : doseLabel(med, 0), styles:{halign:"center", fontSize:fz.base-1, fontStyle: med._stopMed?"bold":"normal", textColor: med._stopMed?[220,38,38]:isPRN(med.freq)?[160,80,0]:[30,30,30]}, _lastDoseRow: totalDoses === 1},
                    ...dates.map(d => {
                      const isActive = isMedActive(med, d);
                      if(med._stopMed) return {content: "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[180,180,180], fillColor:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                      return {content: isActive ? "" : "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[150,150,150], fillColor:isActive?[255,255,255]:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                    })
                  ]);
                  for(let di = 1; di < totalDoses; di++) {
                    const isLastDose = di === totalDoses - 1;
                    body.push([
                      {content: doseLabel(med, di), styles:{halign:"center", fontSize:fz.base-1, textColor: isPRN(med.freq)?[160,80,0]:[30,30,30]}, _lastDoseRow: isLastDose},
                      ...dates.map(d => {
                        const isActive = isMedActive(med, d);
                        if(med._stopMed) return {content: "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[180,180,180], fillColor:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                        return {content: isActive ? "" : "✕", styles:{minCellHeight:fz.cell, halign:"center", textColor:[150,150,150], fillColor:isActive?[255,255,255]:[240,240,240], lineColor:[180,180,180], lineWidth:0.5}};
                      })
                    ]);
                  }
                }
                
                // Add thin gray separator row after each medication (skip after the very last one to avoid blank page)
                const visibleMeds = bucket.meds.filter(m => !namedM.some(x => String(x.alternatesWith)===String(m.id)));
                const isLastMedInDoc = bucket === buckets[buckets.length - 1] && med === visibleMeds[visibleMeds.length - 1];
                if (!isLastMedInDoc) {
                  body.push([
                    {content: "", colSpan: 2 + dates.length, styles:{fillColor:[240,240,240], minCellHeight:1.5, cellPadding:0}}
                  ]);
                }
              }
            });
        });

        // ── Render table ──
        autoTable(pdf, {
          startY: headerY,
          head,
          body,
          margin: {left: margin, right: margin},
          tableWidth: pageW - margin * 2,
          rowPageBreak: "avoid",
          styles: {
            fontSize: fz.base,
            cellPadding: 2,
            overflow: "linebreak",
            valign: "middle",
            textColor: [0,0,0],
            lineColor: [180,180,180],
            lineWidth: 0.5,
            fontStyle: fzKey === "xlarge" ? "bold" : "normal", // Bold for Extra Large (accessibility)
          },
          alternateRowStyles: {
            fillColor: [249, 249, 249]
          },
          headStyles: {
            fillColor: [220,220,220],
            textColor: [0,0,0],
            fontSize: fz.hdr,
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
          },
          columnStyles: {
            0: {cellWidth: (pageW - margin*2) * 0.24, valign:"top"},
            1: {cellWidth: (pageW - margin*2) * 0.08, halign:"center"},
          },
          willDrawCell: (data) => {
            // Force page break before PRN section header if too close to bottom
            if(data.section === "body" && data.column.index === 0 &&
               typeof data.cell.raw === "string" && data.cell.raw.includes("PRN") &&
               data.cursor && (pageH - data.cursor.y) < 220) {
              data.doc.addPage("letter", "landscape");
              data.cursor.y = margin + 30;
            }
          },
          didParseCell: (data) => {
            if(data.section === "body" && typeof data.cell.raw === "string" && data.cell.raw.startsWith("\x00")) {
              data.cell.text = [data.cell.raw.slice(1)];
            }
          },
          didDrawCell: (data) => {
            // Draw X on blocked cells (those with ✕ content) - must be in didDrawCell so it draws AFTER the cell
            if(data.section === "body" && data.column.index >= 2 && data.cell.text[0] === "✕") {
              const cellX = data.cell.x;
              const cellY = data.cell.y;
              const cellW = data.cell.width;
              const cellH = data.cell.height;
              
              pdf.setDrawColor(150, 150, 150);
              pdf.setLineWidth(1.5);
              const padding = Math.min(cellW, cellH) * 0.15;
              pdf.line(cellX + padding, cellY + padding, cellX + cellW - padding, cellY + cellH - padding);
              pdf.line(cellX + cellW - padding, cellY + padding, cellX + padding, cellY + cellH - padding);
            }
          },
          didDrawPage: (data) => {
            const dateRange = `${fmtD(dates[0])} – ${fmtD(dates[dates.length-1])}`;

            // ── Footer on every page ──
            pdf.setDrawColor(200,200,200);
            pdf.setLineWidth(0.3);
            pdf.line(margin, pageH - margin - 22, pageW - margin, pageH - margin - 22);

            try { if(philBase64) pdf.addImage(philBase64, "PNG", margin, pageH - margin - 20, 16, 16); } catch(e) {}

            pdf.setFont("helvetica","bold");
            pdf.setFontSize(8);
            pdf.setTextColor(37,99,235);
            pdf.text("PillPaper™", margin + 20, pageH - margin - 10);
            pdf.setFont("helvetica","normal");
            pdf.setFontSize(6.5);
            pdf.setTextColor(130,130,130);
            pdf.text("pillpaper.com", margin + 20, pageH - margin - 4);

            // Disclaimer on one line, smaller and centered
            pdf.setFontSize(5);
            pdf.setTextColor(100,100,100);
            pdf.text("For personal tracking only. PillPaper is not liable for medication errors.", pageW / 2, pageH - margin - 4, {align:"center"});

            // Page number will be stamped later in footer (right side)
            
            pdf.setTextColor(0);
            pdf.setFont("helvetica","normal");
          },
        });

        // ── Stamp patient info and page numbers into footer (right side) ──
        const weekLastPage = pdf.internal.getCurrentPageInfo().pageNumber;
        const weekFirstPage = pdf.lastAutoTable?.startPageNumber || weekStartPage;
        const weekTotalPages = weekLastPage - weekFirstPage + 1;
        const dateRange = `${fmtD(dates[0])} – ${fmtD(dates[dates.length-1])}`;
        
        for(let p = 0; p < weekTotalPages; p++) {
          const pageNum = weekFirstPage + p;
          pdf.setPage(pageNum);
          
          // Top line far right: Patient name, date, and page number
          pdf.setFont("helvetica","normal");
          pdf.setFontSize(7);
          pdf.setTextColor(0,0,0);
          const topLineText = weekTotalPages > 1
            ? `${printName} (${dateRange})  Page ${p+1} of ${weekTotalPages}`
            : `${printName} (${dateRange})`;
          pdf.text(topLineText, pageW - margin, pageH - margin - 10, {align:"right"});
          
          // Bottom line far right: Printed date
          const now = new Date();
          const printDate = now.toLocaleString('en-US', {month:'numeric', day:'numeric', year:'2-digit'});
          pdf.setFontSize(6);
          pdf.setTextColor(100,100,100);
          pdf.text(`Printed ${printDate}`, pageW - margin, pageH - margin - 4, {align:"right"});
          
          pdf.setTextColor(0);
        }
        pdf.setPage(pdf.internal.pages.length - 1);
      });

      const fileNameBase = (() => {
        if(!patient || !patient.trim()) return "PillPaper_MedLog";
        const parts = patient.trim().replace(/,/g,"").split(/\s+/).filter(Boolean);
        if(parts.length === 1) return `${parts[0]}_MedLog`;
        const first = parts[0];
        const lastInitial = parts[parts.length-1][0].toUpperCase();
        return `${first}_${lastInitial}_MedLog`;
      })();
      const fileName = fileNameBase + ".pdf";
      const pdfBlob = pdf.output('blob');

      // Track PDF generation event
      try { window.va?.track('pdf_generated', { weeks: w, medCount: namedM.length, sortAlpha }); } catch(e) {}

      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const isIosSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);
      const iosHintShownBefore = (() => { try { return !!localStorage.getItem("pillpaper_ios_hint_shown"); } catch(e) { return false; } })();

      if(isMobileDevice) {
        setDeliveryPdf({blob: pdfBlob, fileName});
        setShowPrintConfirm(false);
        setShowDelivery(true);
        if(isIosSafari && !iosHintShownBefore) {
          setTimeout(() => {
            setShowIosHint(true);
            try { localStorage.setItem("pillpaper_ios_hint_shown", "1"); } catch(e) {}
          }, 1500); // slight delay so delivery modal shows first
        }
      } else {
        // Desktop: download directly, no modal
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement("a");
        a.href = url; a.download = fileName; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch(e) {
      console.error(e);
      alert("Could not generate PDF — try again.");
    }
    setPdfBusy(false);
    setShowPrint(false);
  };

  const deliverSave = () => {
    if(!deliveryPdf) return;
    const url = URL.createObjectURL(deliveryPdf.blob);
    const a = document.createElement("a");
    a.href = url; a.download = deliveryPdf.fileName; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setShowDelivery(false);
  };

  const deliverEmail = async () => {
    if(!deliveryPdf) return;
    const file = new File([deliveryPdf.blob], deliveryPdf.fileName, {type:"application/pdf"});
    if(navigator.canShare && navigator.canShare({files:[file]})) {
      try { await navigator.share({files:[file], title:"Medication Log", text:"My medication log from PillPaper"}); } catch(e) {}
    } else {
      // Fallback: download and instruct
      deliverSave();
    }
    setShowDelivery(false);
  };

  const deliverPrint = async () => {
    if(!deliveryPdf) return;
    const file = new File([deliveryPdf.blob], deliveryPdf.fileName, {type:"application/pdf"});
    if(navigator.canShare && navigator.canShare({files:[file]})) {
      try { await navigator.share({files:[file], title:"Medication Log", text:"My medication log from PillPaper"}); } catch(e) {}
    } else {
      deliverSave();
    }
    setShowDelivery(false);
  };

  const print = () => {
    setShowPrintConfirm(true);
  };

  const logCustomFreq = async (freq, medName) => {
    try {
      const key = "marfill_custom_freqs"; let ex = [];
      try { const r = await lsGet(key); if(r) ex = JSON.parse(r.value); } catch(e) {}
      if(!ex.find(e => e.freq===freq)) { ex.push({freq, date:new Date().toISOString().slice(0,10), med:medName}); await lsSet(key, JSON.stringify(ex)); }
    } catch(e) {}
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const MAX = 1024;
        let w = img.width, h = img.height;
        if(w>MAX || h>MAX) { if(w>h) { h=Math.round(h*MAX/w); w=MAX; } else { w=Math.round(w*MAX/h); h=MAX; } }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if(ctx) ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.8).split(",")[1]);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  };

  const applyResult = (i, result, rawFreq, parsed, fromImage=false) => {
    const freqLabel = result ? result.freq : OTHER_FREQ;
    const existingFreq = meds[i].freq;
    // If AI couldn't identify frequency, keep existing freq rather than populating OTHER_FREQ garbage
    const resolvedFreqLabel = (freqLabel === OTHER_FREQ && existingFreq && existingFreq !== OTHER_FREQ)
      ? existingFreq : freqLabel === OTHER_FREQ ? "" : freqLabel;
    const prn = isPRN(resolvedFreqLabel);
    const intv = isInterval(resolvedFreqLabel);
    const rawMax = result && result.max != null ? result.max : (parsed.maxDailyDoses ? parseInt(parsed.maxDailyDoses) : null);
    const max = prn ? rawMax : null;
    const timing = result ? result.timing : null;
    const scheduledDay = result ? (result.scheduledDay || null) : null;
    const f = FREQ_OPTIONS.find(x => x.label===resolvedFreqLabel);
    const doses = intv ? 1 : (prn && max) ? max : (f ? f.doses : 1);

    // Detect if AI calculated a fractional dose (e.g. returned 12.5mg when label says 25mg take 0.5 tab)
    // If qty contains a fraction (0.5, 1/2, 1.5 etc), reverse-calculate the label strength from AI's dose
    let dose = parsed.dose || "";
    const qty = parsed.qty || "";
    const fracMatch = qty.match(/^(\d*\.?\d+)\s*tablet/i);
    if(fracMatch && dose) {
      const qtyNum = parseFloat(fracMatch[1]);
      if(qtyNum > 0 && qtyNum !== 1 && qtyNum < 1) {
        // AI likely calculated: label_strength × qty = dose they returned
        // Reverse: label_strength = dose / qty
        const doseNum = parseFloat(dose);
        const unit = dose.replace(/[\d.]/g,"").trim();
        if(!isNaN(doseNum) && !isNaN(qtyNum)) {
          const labelStrength = Math.round((doseNum / qtyNum) * 1000) / 1000;
          dose = labelStrength + unit;
        }
      }
    }

    const rawName = (parsed.name || "")
      .replace(/\s*\(.*?\)\s*/g, "")
      .replace(/\s*(oral tablet|oral capsule|oral liquid|oral solution|nasal spray|topical|tablet|capsule)\s*$/i, "")
      .replace(/\bHCI\b/gi, "HCL")  // Common OCR error: HCI → HCL (hydrochloride)
      .trim();
    const name = dose ? rawName.replace(new RegExp("\\s*" + dose.replace(/[.*+?^${}()|[\]\\]/g,"\\$&") + "\\s*$","i"),"").trim() : rawName;
    
    // Check for duplicate medication name (skip for nutritional products)
    const isNutrition = parsed._isNutrition || false;
    if(!isNutrition) {
      const isDuplicate = meds.some((m, idx) => idx !== i && m.name.toLowerCase().trim() === name.toLowerCase().trim() && m.name.trim() !== "");
      if(isDuplicate) {
        const proceed = window.confirm(`"${name}" is already in your medication list. Add it again anyway?\n\nClick OK to add this duplicate, or Cancel to skip.`);
        if(!proceed) {
          setAiIdx(null);
          return;
        }
      }
    }
    
    // Handle patient name - auto-populate and warn on mismatch
    if(parsed.patientName && parsed.patientName.trim()) {
      const newPatientName = parsed.patientName.trim();
      // Normalize names for comparison: strip commas, normalize spaces, handle "LAST, FIRST" → "FIRST LAST"
      const normalizeName = (n) => {
        let s = n.toLowerCase().replace(/,/g,"").replace(/\s+/g," ").trim();
        // If format was "last, first" the comma removal gives "last first" — sort words to compare
        return s.split(" ").sort().join(" ");
      };
      const sameNormalized = normalizeName(patient) === normalizeName(newPatientName);
      const formatPatientName = (n) => {
        const cleaned = n.trim();
        if(cleaned.includes(",")) {
          const [last, first] = cleaned.split(",").map(s => s.trim());
          const toTitle = s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
          return `${toTitle(first)} ${toTitle(last)}`;
        }
        return cleaned.split(/\s+/).map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(" ");
      };
      if(!patient) {
        setPatient(formatPatientName(newPatientName));
      } else if(!sameNormalized) {
        // Check approved aliases using normalized comparison so format differences don't re-trigger
        const normalizedPatient = normalizeName(patient);
        const normalizedNew = normalizeName(newPatientName);
        const alreadyApproved = approvedNameAliases.some(
          pair => normalizeName(pair[0]) === normalizedPatient && normalizeName(pair[1]) === normalizedNew
        ) || approvedNameAliases.some(
          pair => normalizeName(pair[0]) === normalizedNew && normalizeName(pair[1]) === normalizedPatient
        );
        if(!alreadyApproved) {
          const choice = window.confirm(
            `⚠️ Different patient name detected!\n\nCurrent: ${patient}\nOn this bottle: ${newPatientName}\n\nThis bottle may be for a different person.\n\nClick OK to add this medication and suppress this warning for "${newPatientName}" going forward.\nClick Cancel to skip this bottle.`
          );
          if(!choice) {
            setAiIdx(null);
            return;
          }
          setApprovedNameAliases(prev => [...prev, [patient, newPatientName]]);
        }
      }
    }
    
    const c = [...meds];
    c[i] = {...c[i], name:name||c[i].name, brand:parsed.brand||"", dose, qty:parsed.qty||"",
      freq:resolvedFreqLabel, customFreq:resolvedFreqLabel===OTHER_FREQ ? (rawFreq||"") : "", doses, maxDailyDoses:max, prnTiming:timing, scheduledDays:scheduledDay?[scheduledDay]:(c[i].scheduledDays||[]), _isNutrition:isNutrition||false,
      notes:cleanNotes(parsed.notes||""), 
      additionalNotes: parsed.additionalNotes ? cleanNotes(parsed.additionalNotes) : (c[i].additionalNotes||""),
      duration:parsed.duration||"", 
      refillsLeft:parsed.refillsLeft||"", prescriber:parsed.prescriber||"", rxNumber:parsed.rxNumber||"", pillDescription:parsed.pillDescription||"",
      _aiParsed:true};
    setMeds(c);
  };

  const callAI = async (i, messages) => {
    setAiIdx(i);
    try {
      const data = await callAnthropic({model:"claude-sonnet-4-6", max_tokens:400, system:AI_SYSTEM, messages});
      const txt = (data.content && data.content.find(b => b.type==="text") ? data.content.find(b => b.type==="text").text : "").trim();
      const s = txt.indexOf("{"), e = txt.lastIndexOf("}");
      if(s===-1 || e===-1) throw new Error("no JSON");
      const parsed = JSON.parse(txt.slice(s, e+1));
      applyResult(i, normalizeFreq((parsed.freq||"").trim()), (parsed.freq||"").trim(), parsed);
    } catch(e) { console.error(e); }
    setAiIdx(null);
  };

  const parseText = (i) => {
    if(!meds[i].name.trim()) return;
    callAI(i, [{role:"user", content:AI_PROMPT+"\n\nExtract from: \""+meds[i].name+"\""}]);
  };

  const parseImage = async (i, file, file2=null) => {
    // Check AI scan limit
    if(aiScansUsed >= AI_SCAN_LIMIT) {
      alert(`You've used all ${AI_SCAN_LIMIT} AI scans this session. Refresh the page for more, or enter medications manually.`);
      return;
    }
    // Block if bulk import is already running
    if(bulkBusy) {
      alert("Please wait — a medication list is currently being read. Try again when it finishes.");
      return;
    }
    
    setAiIdx(i);
    setPendingPhotos(prev => { const n={...prev}; delete n[i]; return n; });
    try {
      const b64 = await compressImage(file);
      if(!b64) { alert("Could not read image — try again."); setAiIdx(null); return; }
      const content = [];
      content.push({type:"image", source:{type:"base64", media_type:"image/jpeg", data:b64}});
      if(file2) {
        const b64b = await compressImage(file2);
        if(b64b) content.push({type:"image", source:{type:"base64", media_type:"image/jpeg", data:b64b}});
      }

      const prompt = file2
        ? AI_PROMPT + "\n\nExtract from these two photos of the same bottle label — combine both images to get all details. IMPORTANT for freq: extract ONLY the dosing schedule (e.g. 'twice daily', 'every 8 hours', 'once a day at bedtime') — do NOT include route or quantity in freq."
        : AI_PROMPT + "\n\nExtract from this bottle label. This may be a prescription bottle OR an OTC product. For OTC items with no specific frequency on the label, use your best judgment from the directions. Do your best with what's visible. IMPORTANT for freq: extract ONLY the dosing schedule (e.g. 'twice daily', 'every 8 hours', 'once a day at bedtime') — do NOT include route or quantity in freq. If directions say 'take one tablet by mouth twice daily', freq is 'twice daily' and qty is '1 tablet by mouth'.";
      content.push({type:"text", text:prompt});
      const data = await callAnthropic({model:"claude-sonnet-4-6", max_tokens:400, system:AI_SYSTEM, messages:[{role:"user", content}]});
      const txt = (data.content?.find(b => b.type==="text")?.text || "").trim();
      const s = txt.indexOf("{"), e = txt.lastIndexOf("}");
      if(s===-1 || e===-1) { alert("Could not read label — try again."); setAiIdx(null); return; }
      const parsed = JSON.parse(txt.slice(s, e+1));

      // Check if AI found anything useful
      if(!parsed.name?.trim() && !parsed.brand?.trim()) {
        alert("Couldn't find medication information in this photo.\n\nTips:\n• Make sure the label is in focus and well-lit\n• Hold your phone upright (portrait)\n• Try getting closer to the label");
        setAiIdx(null);
        return;
      }
      
      // Increment scan counter on success
      setAiScansUsed(prev => prev + 1);
      
      applyResult(i, normalizeFreq((parsed.freq||"").trim()), (parsed.freq||"").trim(), parsed, true);
    } catch(e) { alert("Could not read label — please try again or enter manually."); }
    setAiIdx(null);
  };

  const parseBulk = async () => {
    if(!bulk.trim()) return;
    
    // Check AI scan limit
    if(aiScansUsed >= AI_SCAN_LIMIT) {
      setBulkMsg(`You've used all ${AI_SCAN_LIMIT} AI scans this session. Refresh the page for more, or enter medications manually.`);
      return;
    }
    
    setBulkBusy(true); setBulkMsg("");
    try {
      const data = await callAnthropic({model:"claude-sonnet-4-6", max_tokens:4000, system:AI_SYSTEM,
        messages:[{role:"user", content:"Extract ALL medications. Return ONLY a raw JSON array starting with [ and ending with ]. Each item: {name, brand, dose, qty, freq, maxDailyDoses, notes, additionalNotes, duration, stopMed}. stopMed: true if this medication appears under a STOP taking, Discontinue, or Do NOT take section in the document. false otherwise. name: generic name only, no strength, no parentheticals — extract the generic drug name only (e.g. 'Cyanocobalamin (VITAMIN B 12 PO)' → name: 'cyanocobalamin'; 'Multiple Vitamin (MULTIVITAMIN)' → name: 'multivitamin'; 'Cholecalciferol (VITAMIN D-3 PO)' → name: 'cholecalciferol'; 'MAGNESIUM OXIDE, ELEMENTAL, PO' → name: 'magnesium oxide'). IMPORTANT: preserve salt forms and release modifiers in the name (e.g. 'metoprolol succinate ER' stays 'metoprolol succinate ER'). brand: CRITICAL — if the text lists a brand name anywhere for this medication, you MUST capture it in the brand field. Brand names are typically capitalized trade names (e.g. 'Gastrocrom', 'Zyrtec', 'Prilosec', 'Zyflo CR'). Never let the generic name conversion cause you to drop a brand name that is explicitly written in the text. If text says 'Commonly known as: X', use X as brand. For vitamins with parentheticals like 'Cyanocobalamin (VITAMIN B 12 PO)' → brand: 'Vitamin B12'; 'Cholecalciferol (VITAMIN D-3 PO)' → brand: 'Vitamin D3'; 'Multiple Vitamin (MULTIVITAMIN)' → brand: 'Multivitamin'. If no brand name appears anywhere in the text for this drug, brand is empty string. dose: strength only, concise (e.g. '500mg', '1000mcg', '17g'). qty: exact count and form AND route (e.g. '1 capsule by mouth') — never use mg or g as qty unless it is the dispensing unit like a packet. freq: the dosing interval ONLY — ignore any quantities embedded in the frequency description. IMPORTANT: if directions say 'X in the morning and X in the evening' (even with quantities like '17g in the morning and 17g in the evening'), use freq 'in the morning and in the evening' as a SINGLE entry. If directions say 'in the morning and at bedtime' or 'in the morning and before bedtime', use freq 'in the morning and at bedtime' as a SINGLE entry. For compound frequency instructions like '4x daily 30 min before meals', use the standard interval (e.g. 'four times daily') and ALWAYS put the timing qualifier in additionalNotes (e.g. additionalNotes: '30 min before meals', or '30 minutes before levothyroxine'). For 'with lunch' or 'with breakfast' etc, use freq 'with lunch' or 'with breakfast'. For PRN/as-needed: extract ONLY the interval (e.g. 'every 8 hours as needed'). Strip any reason from freq. notes: the reason or indication for taking this medication — use a concise but complete phrase up to 8 words (e.g. 'GERD and mast cell symptoms', 'H1 blocker for itching and flushing', 'stabilize mast cells in GI tract'). Include ALL listed reasons separated by slash or comma. Do NOT reduce to a single word if more context is available. additionalNotes: any special timing instructions or auxiliary warnings (e.g. '30 min before meals', '30 minutes before levothyroxine', 'take with food', 'avoid sun'). Empty string if none. duration: number of days if stated else empty string. Do not duplicate medications that appear twice in the text. No markdown.\n\nText:\n"+bulk}]
      });
      if(data.error) { setBulkMsg(data.error.type==="overloaded_error" ? "AI is busy right now — wait a few seconds and try again." : data.error.type==="rate_limit" ? "You've reached today's limit — please try again later." : "Error: "+data.error.message); setBulkBusy(false); return; }
      
      // Increment scan counter on success
      setAiScansUsed(prev => prev + 1);
      
      const raw = (data.content && data.content.find(b => b.type==="text") ? data.content.find(b => b.type==="text").text : "").trim();
      const s = raw.indexOf("["), e = raw.lastIndexOf("]");
      if(s===-1 || e===-1) { setBulkMsg("Could not find medication list — try again."); setBulkBusy(false); return; }
      const parsed = JSON.parse(raw.slice(s, e+1));
      
      // Check if any entries mention blood sugar/glucose monitoring and extract frequency
      const glucoseEntry = parsed.find(m => 
        /blood sugar|glucose|test.*sugar|check.*sugar|monitor.*sugar/i.test((m.name||"") + " " + (m.notes||""))
      );
      const glucoseFreq = glucoseEntry ? (glucoseEntry.freq || "Three times daily") : null;
      
      // Filter out glucose monitoring supplies (test strips, lancets, readers, sensors, meters)
      const medicationsOnly = parsed.filter(m => {
        const fullText = (m.name || "") + " " + (m.notes || "");
        const isGlucoseSupply = /test strip|lancet|reader|sensor|meter|glucose monitor|freestyle libre|onetouch|dexcom|contour/i.test(fullText);
        return !isGlucoseSupply;
      });
      
      const added = medicationsOnly
        .filter(m => m.name && m.name.trim() && !/unable to parse|could not parse|unknown medication|error/i.test(m.name))
        .map(m => {
        const rawFreq = (m.freq||"").trim();
        const result = rawFreq ? normalizeFreq(rawFreq) : null;
        const freqLabel = result ? result.freq : (rawFreq ? OTHER_FREQ : "");
        const prn = isPRN(freqLabel);
        const intv = isInterval(freqLabel);
        const rawMax = result && result.max != null ? result.max : (m.maxDailyDoses ? parseInt(m.maxDailyDoses) : null);
        const max = prn ? rawMax : null;
        const timing = result ? result.timing : null;
        const scheduledDay = result ? (result.scheduledDay || null) : null;
        const scheduledDaysArr = result?.scheduledDays || (scheduledDay ? [scheduledDay] : []);
        const f = FREQ_OPTIONS.find(x => x.label===freqLabel);
        const doses = intv ? 1 : (prn && max) ? max : (f ? f.doses : 1);
        return {id:Date.now()+Math.random(), name:m.name||"", brand:m.brand||"", dose:m.dose||"", qty:m.qty||"",
          freq:freqLabel, customFreq:rawFreq||"", doses, maxDailyDoses:max, prnTiming:timing, scheduledDays:scheduledDaysArr,
          notes:cleanNotes(m.notes||""), additionalNotes:m.additionalNotes||"",
          duration: (() => { const n=parseInt(m.duration||""); if(isNaN(n)||n<=0||n>=30) return ""; return n+" days"; })(),
          alternatesWith:null, _altChecked:false, _aiParsed:true, _bulkImported:true, dateStarted:"", _stopMed:m.stopMed||false};
      });
      
      // Auto-enable glucose tracking if monitoring instructions detected
      if (glucoseFreq) {
        setVitals(prev => ({...prev, glucose: glucoseFreq}));
      }
      
      // Deduplicate within the imported batch
      const seen = new Set();
      const deduped = added.filter(m => {
        const key = (m.name + "|" + m.dose).toLowerCase().trim();
        if(seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Flag any that already exist in the current med list
      setMeds(prev => {
        const existing = prev.filter(m => m.name.trim());
        if(existing.length > 0 && deduped.length > 0) {
          const dupCount = deduped.filter(m =>
            existing.some(e => e.name.toLowerCase().trim() === m.name.toLowerCase().trim() && e.dose.toLowerCase().trim() === m.dose.toLowerCase().trim())
          ).length;
          if(dupCount / deduped.length >= 0.75) {
            const proceed = window.confirm(`⚠️ This looks like a list you already imported — ${dupCount} of ${deduped.length} medications are already in your list.\n\nAdd them anyway?`);
            if(!proceed) return prev;
          }
        }
        const flagged = deduped.map(m => {
          const isDup = existing.some(e => e.name.toLowerCase().trim() === m.name.toLowerCase().trim() && e.dose.toLowerCase().trim() === m.dose.toLowerCase().trim());
          return isDup ? {...m, _duplicateWarning: true} : m;
        });
        return [...existing, ...flagged];
      });
      if(deduped.length === 0) {
        setBulkMsg("Couldn't read any medications — check your text and try again.");
      } else {
        setBulk(""); setBulkMsg(deduped.length+" medication"+(deduped.length!==1?"s":"")+" added!");
      }
      setTimeout(() => setBulkMsg(""), 8000);
    } catch(e) { setBulkMsg("Error — try again."); }
    setBulkBusy(false);
  };

  const parseBulkImage = async (file1, file2=null) => {
    if(!file1) return;
    
    // Check AI scan limit
    if(aiScansUsed >= AI_SCAN_LIMIT) {
      setBulkMsg(`You've used all ${AI_SCAN_LIMIT} AI scans this session. Refresh the page for more, or enter medications manually.`);
      return;
    }
    // Block if a bottle scan is already running
    if(aiIdx !== null) {
      setBulkMsg("Please wait — a bottle scan is currently in progress.");
      return;
    }
    
    setBulkBusy(true); setBulkMsg("");
    try {
      const b64a = await compressImage(file1);
      if(!b64a) { setBulkMsg("Could not read image — try again."); setBulkBusy(false); return; }
      const content = [
        {type:"image", source:{type:"base64", media_type:"image/jpeg", data:b64a}},
      ];
      if(file2) {
        const b64b = await compressImage(file2);
        if(b64b) content.push({type:"image", source:{type:"base64", media_type:"image/jpeg", data:b64b}});
      }
      content.push({type:"text", text:`Extract ALL medications from ${file2 ? "these two images of the same document — combine both pages" : "this image"}. Return ONLY a raw JSON array starting with [ and ending with ]. Each item: {name, brand, dose, qty, freq, maxDailyDoses, notes, duration, hasHandwriting, stopMed}. Rules: stopMed: true if this medication appears under a "STOP taking" or "Discontinue" or "Do NOT take" section header in the document. false otherwise. name: generic name only, no strength, no parentheticals. brand: CRITICAL — if the document lists a brand name anywhere for this medication, you MUST capture it in the brand field. The brand name is typically the capitalized trade name (e.g. 'Gastrocrom', 'Zyrtec', 'Zantac', 'Prilosec', 'Zyflo CR'). Even if a Generic checkbox is checked, the brand name printed in the document must still be extracted. If the row says 'Gastrocrom 100mg ampules', brand is 'Gastrocrom' and name is 'cromolyn sodium'. Never let the generic conversion cause you to drop a brand name that is explicitly written in the document. If no brand name is listed anywhere in the document for this drug, brand is empty string. dose: strength only, concise (e.g. '500mg', '2%'). qty: how many units AND route — use the exact count and form from the document (e.g. '1 capsule by mouth', '1 tablet by mouth', '1 application topical', '0.5 milligrams subcutaneous') — never use mg as qty. freq: the dosing interval ONLY — never include the reason or indication in freq. For PRN/as-needed: extract ONLY the interval (e.g. 'every 4 hours as needed', 'every 6 hours as needed', 'once a day as needed', 'every 24 hours as needed'). Strip any reason/indication text from freq entirely — e.g. if document says 'Every 4 hours as needed for Pain' → freq is 'every 4 hours as needed', notes is 'Pain'. For compound frequency instructions like '4x daily 30 min before meals', use the standard interval (e.g. 'four times daily') and ALWAYS put the timing qualifier in additionalNotes (e.g. additionalNotes: '30 min before meals', or '30 minutes before levothyroxine'). For 'with lunch' or 'with breakfast' etc, use freq 'with lunch' or 'with breakfast'. maxDailyDoses: for PRN meds only, extract the maximum number of doses allowed (e.g. "up to 4 doses" → 4, "may repeat x1" → 2, "max 3 doses" → 3, "seizure up to 4 doses" → 4). If no max is stated, null. notes: the reason or indication for taking this medication — use a concise but complete phrase up to 8 words (e.g. 'GERD and mast cell symptoms', 'H1 blocker for itching and flushing'). Include ALL listed reasons separated by slash or comma. Do NOT reduce to a single word if more context is available. No frequency, no route, no 'as needed for' prefix. additionalNotes: any special timing instructions or auxiliary warnings (e.g. '30 min before meals', '30 minutes before levothyroxine', 'take with food', 'avoid sun'). Empty string if none. duration: number of days if stated (e.g. '7 days', '14 days') else empty string. hasHandwriting: true ONLY if handwritten text directly modifies the printed medication information in the medication description column — examples that SHOULD flag: "STOPPED" written next to a med name, a crossed-out drug name, handwritten dose correction like "2 tabs" written over "1 tab". NEVER flag for: checkmarks (✓), times (8A, 5P, 12pm, 8P), numbers in dose-tracking cells, X marks, slashes, initials, or ANY writing in the date columns or dose-recording grid cells to the right of the medication name. The dose-recording area is for tracking when meds were given — writing there is never a medication change. If in doubt, return false. Do not skip any medications. No markdown.`});
      const data = await callAnthropic({model:"claude-sonnet-4-6", max_tokens:4000, system:AI_SYSTEM,
        messages:[{role:"user", content}]
      });
      if(data.error) { setBulkMsg(data.error.type==="overloaded_error" ? "AI is busy right now — wait a few seconds and try again." : data.error.type==="rate_limit" ? "You've reached today's limit — please try again later." : "Error: "+data.error.message); setBulkBusy(false); return; }
      
      // Increment scan counter on success
      setAiScansUsed(prev => prev + 1);
      
      const raw = (data.content && data.content.find(b => b.type==="text") ? data.content.find(b => b.type==="text").text : "").trim();
      const s = raw.indexOf("["), e = raw.lastIndexOf("]");
      if(s===-1 || e===-1) { 
        setBulkMsg("Could not read the full list — if there are many medications, try splitting into two separate imports."); 
        setBulkBusy(false); return; 
      }
      let parsed;
      try { parsed = JSON.parse(raw.slice(s, e+1)); }
      catch(jsonErr) { setBulkMsg("List was too long to process at once — try splitting into two separate imports."); setBulkBusy(false); return; }
      
      // Check if any entries mention blood sugar/glucose monitoring and extract frequency
      const glucoseEntry = parsed.find(m => 
        /blood sugar|glucose|test.*sugar|check.*sugar|monitor.*sugar/i.test((m.name||"") + " " + (m.notes||""))
      );
      const glucoseFreq = glucoseEntry ? (glucoseEntry.freq || "Three times daily") : null;
      
      // Filter out glucose monitoring supplies (test strips, lancets, readers, sensors, meters)
      const medicationsOnly = parsed.filter(m => {
        const fullText = (m.name || "") + " " + (m.notes || "");
        const isGlucoseSupply = /test strip|lancet|reader|sensor|meter|glucose monitor|freestyle libre|onetouch|dexcom|contour/i.test(fullText);
        return !isGlucoseSupply;
      });
      
      const added = medicationsOnly
        .filter(m => m.name && m.name.trim() && !/unable to parse|could not parse|unknown medication|error/i.test(m.name))
        .map(m => {
        const rawFreq = (m.freq||"").trim();
        const result = rawFreq ? normalizeFreq(rawFreq) : null;
        const freqLabel = result ? result.freq : (rawFreq ? OTHER_FREQ : "");
        const prn = isPRN(freqLabel);
        const intv = isInterval(freqLabel);
        const rawMax = result && result.max != null ? result.max : (m.maxDailyDoses ? parseInt(m.maxDailyDoses) : null);
        const max = prn ? rawMax : null;
        const timing = result ? result.timing : null;
        const scheduledDay = result ? (result.scheduledDay || null) : null;
        const scheduledDaysArr = result?.scheduledDays || (scheduledDay ? [scheduledDay] : []);
        const f = FREQ_OPTIONS.find(x => x.label===freqLabel);
        const doses = intv ? 1 : (prn && max) ? max : (f ? f.doses : 1);
        return {id:Date.now()+Math.random(), name:m.name||"", brand:m.brand||"", dose:m.dose||"", qty:m.qty||"",
          freq:freqLabel, customFreq:rawFreq||"", doses, maxDailyDoses:max, prnTiming:timing, scheduledDays:scheduledDaysArr,
          notes:cleanNotes(m.notes||""), additionalNotes:m.additionalNotes||"",
          duration: (() => { const n=parseInt(m.duration||""); if(isNaN(n)||n<=0||n>=30) return ""; return n+" days"; })(),
          alternatesWith:null, _altChecked:false, _aiParsed:true, _bulkImported:true, dateStarted:"", hasHandwriting:m.hasHandwriting||false, _stopMed:m.stopMed||false};
      });
      
      // Auto-enable glucose tracking if monitoring instructions detected
      if (glucoseFreq) {
        setVitals(prev => ({...prev, glucose: glucoseFreq}));
      }
      
      // Deduplicate by name+dose
      const seen = new Set();
      const deduped = added.filter(m => {
        const key = (m.name + "|" + m.dose).toLowerCase().trim();
        if(seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Check if this looks like a duplicate import (75%+ overlap with existing meds)
      setMeds(prev => {
        const existing = prev.filter(m => m.name.trim());
        if(existing.length > 0 && deduped.length > 0) {
          const dupCount = deduped.filter(m =>
            existing.some(e => e.name.toLowerCase().trim() === m.name.toLowerCase().trim() && e.dose.toLowerCase().trim() === m.dose.toLowerCase().trim())
          ).length;
          if(dupCount / deduped.length >= 0.75) {
            const proceed = window.confirm(`⚠️ This looks like a list you already imported — ${dupCount} of ${deduped.length} medications are already in your list.\n\nAdd them anyway?`);
            if(!proceed) return prev;
          }
        }
        const flagged = deduped.map(m => {
          const isDup = existing.some(e => e.name.toLowerCase().trim() === m.name.toLowerCase().trim() && e.dose.toLowerCase().trim() === m.dose.toLowerCase().trim());
          return isDup ? {...m, _duplicateWarning: true} : m;
        });
        return [...existing, ...flagged];
      });
      setBulkFile1(null); setBulkFile2(null); setBulkPastedImg(null);
      if(deduped.length === 0) {
        setBulkMsg("Couldn't read any medications from this image — try a clearer photo or use Type or Paste instead.");
      } else {
        setBulkMsg(deduped.length+" medication"+(deduped.length!==1?"s":"")+" added!");
      }
      setTimeout(() => setBulkMsg(""), 8000);
    } catch(e) { setBulkMsg("Error reading image — try again."); }
    setBulkBusy(false);
  };

  const setMed = (i, field, val) => {
    const c = [...meds]; c[i] = {...c[i], [field]:val};
    if(field==="freq") {
      const result = normalizeFreq(val);
      const freqLabel = result ? result.freq : val;
      const f = FREQ_OPTIONS.find(x => x.label===freqLabel);
      c[i].freq = freqLabel;
      c[i].doses = isInterval(freqLabel) ? 1 : (f ? f.doses : 1);
      if(val!==OTHER_FREQ) c[i].customFreq = "";
      c[i].scheduledDays = [];
    }
    if(field==="customFreq" && val.trim()) logCustomFreq(val, c[i].name);
    setMeds(c);
  };

  const submitFeedback = async () => {
    if(!fbForm.what.trim() && !fbForm.wrong.trim()) return;
    setFbStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xykbzwlw", {
        method:"POST",
        headers:{"Content-Type":"application/json","Accept":"application/json"},
        body: JSON.stringify({
          "What were you trying to do?": fbForm.what,
          "What went wrong / idea to share?": fbForm.wrong,
          "Device / browser": fbForm.device,
          "Email (optional)": fbForm.email,
        })
      });
      if(res.ok) { setFbStatus("sent"); }
      else setFbStatus("error");
    } catch(e) { setFbStatus("error"); }
  };

  const submitShareFeedback = async () => {
    if(!shareFeedbackForm.feedback.trim()) return;
    setShareFeedbackStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xykbzwlw", {
        method:"POST",
        headers:{"Content-Type":"application/json","Accept":"application/json"},
        body: JSON.stringify({
          "Type": "TESTIMONIAL",
          "Feedback": shareFeedbackForm.feedback,
          "Name": shareFeedbackForm.name || "Anonymous",
          "Role": shareFeedbackForm.role || "",
        })
      });
      if(res.ok) { setShareFeedbackStatus("sent"); }
      else setShareFeedbackStatus("error");
    } catch(e) { setShareFeedbackStatus("error"); }
  };

  const fz = FONT[fzKey];
  const allDates = makeDates(start, 30);
  const totalDays = printDays || (printWeeks * 7);
  const trimmedDates = allDates.slice(0, totalDays);
  const weeks = [];
  for(let i = 0; i < trimmedDates.length; i += 7) weeks.push(trimmedDates.slice(i, i+7));
  const namedM = meds.filter(m => m.name.trim());

  const inp = (ex) => ({style:{width:"100%",boxSizing:"border-box",padding:"7px 9px",border:"1px solid #ccc",borderRadius:"6px",fontSize:13,background:"#fff",color:"#111",...(ex && ex.style ? ex.style : {})}});
  const lbl = (t) => <div style={{fontSize:12,color:"#444",fontWeight:500,marginBottom:3}}>{t}</div>;
  const card = {background:"#fff",border:"1px solid #e5e5e5",borderRadius:"10px",padding:"1rem 1.25rem",marginBottom:"1rem"};

  return (
    <div style={{maxWidth:820,margin:"0 auto",padding:"1rem 1rem 3rem",fontFamily:"system-ui,sans-serif",background:"#f8f8f8",minHeight:"100vh"}}>

      {/* ── BETA WELCOME MODAL ── */}
      {showBetaModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:440,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{textAlign:"center",marginBottom:12}}>
              <img src="/phil.png" alt="Phil" style={{width:56,height:56,objectFit:"contain"}}/>
            </div>
            <div style={{fontSize:17,fontWeight:700,color:"#111",marginBottom:6,textAlign:"center"}}>Welcome to PillPaper™ Beta</div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.7,marginBottom:14}}>
              PillPaper is currently in beta testing. A few things to know:
            </div>
            <div style={{fontSize:12,color:"#444",lineHeight:1.8,marginBottom:16,background:"#f8faff",borderRadius:8,padding:"10px 14px",border:"1px solid #e8f0fe"}}>
              🔒 <strong>Your data stays on your device.</strong> We don't store your medication information on any server.<br/>
              🤖 <strong>AI may make mistakes.</strong> Always verify medication details against your original prescription or pill bottle.<br/>
              🧪 <strong>This is beta software.</strong> You may encounter bugs. Use the "Report an issue" button to let us know.<br/>
              📋 <strong>Not medical advice.</strong> PillPaper is a tracking tool only. Always follow your doctor's or pharmacist's instructions.
            </div>
            <div style={{fontSize:11,color:"#888",marginBottom:14,textAlign:"center"}}>
              By continuing you agree to our{" "}
              <span onClick={() => setShowTerms(true)} style={{color:"#2563eb",cursor:"pointer",textDecoration:"underline"}}>Terms of Use</span>
              {" "}and{" "}
              <span onClick={() => setShowPrivacy(true)} style={{color:"#2563eb",cursor:"pointer",textDecoration:"underline"}}>Privacy Policy</span>.
            </div>
            <button onClick={() => {
              try { localStorage.setItem("pillpaper_beta_seen","1"); } catch(e) {}
              setShowBetaModal(false);
            }} style={{width:"100%",padding:"11px",fontSize:14,fontWeight:700,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>
              Got it — let's build my log →
            </button>
          </div>
        </div>
      )}

      {/* ── PRIVACY POLICY MODAL ── */}
      {showPrivacy && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:3001,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:520,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:16,fontWeight:700,color:"#111"}}>Privacy Policy</div>
              <button onClick={() => setShowPrivacy(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#888"}}>✕</button>
            </div>
            <div style={{fontSize:12,color:"#555",lineHeight:1.8}}>
              <p><strong>Last updated: March 2026</strong></p>
              <p><strong>Your data stays on your device.</strong> PillPaper does not collect, store, transmit, or share any medication information you enter. All data is saved locally in your browser using localStorage and is never sent to our servers.</p>
              <p><strong>AI processing.</strong> When you use the AI features (pill bottle scanning, discharge paper import, or medication text entry), the text or image is sent to Anthropic's Claude API for processing. This data is subject to <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#2563eb"}}>Anthropic's Privacy Policy</a>. PillPaper does not retain or log this data.</p>
              <p><strong>Feedback form.</strong> If you submit feedback via the "Report an issue" form, your message is sent through Formspree to the PillPaper developer's email. This data is not shared with third parties.</p>
              <p><strong>No accounts required.</strong> PillPaper does not require you to create an account or provide any personal information to use the service.</p>
              <p><strong>Analytics.</strong> PillPaper uses <a href="https://vercel.com/analytics" target="_blank" rel="noopener noreferrer" style={{color:"#2563eb"}}>Vercel Analytics</a> to collect anonymous, aggregated data about site visits — including page views, device type, browser, and country. This data is cookieless, contains no personal information, and no medication data is ever included. It is used solely to understand how PillPaper is being used and to improve the service. Vercel Analytics does not use advertising trackers or cross-site fingerprinting.</p>
              <p><strong>Children.</strong> PillPaper is not directed at children under 13. If you are using PillPaper to manage a child's medications, no data is collected or stored beyond your device.</p>
              <p><strong>Contact.</strong> Questions about this policy? Use the Contact link in the app footer.</p>
            </div>
            <button onClick={() => setShowPrivacy(false)} style={{width:"100%",padding:"9px",marginTop:16,fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>Close</button>
          </div>
        </div>
      )}

      {/* ── TERMS OF USE MODAL ── */}
      {showTerms && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:3001,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:520,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:16,fontWeight:700,color:"#111"}}>Terms of Use</div>
              <button onClick={() => setShowTerms(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#888"}}>✕</button>
            </div>
            <div style={{fontSize:12,color:"#555",lineHeight:1.8}}>
              <p><strong>Last updated: March 2026</strong></p>
              <p><strong>1. Not medical advice.</strong> PillPaper is a personal medication tracking tool. It is not a substitute for professional medical advice, diagnosis, or treatment. Always follow the instructions of your doctor, pharmacist, or other qualified healthcare provider. Never rely solely on PillPaper to manage your medications.</p>
              <p><strong>2. AI accuracy.</strong> The AI features in PillPaper may produce errors when reading pill bottle labels or discharge paperwork. You are responsible for verifying all medication information — including name, strength, dose, and frequency — against your original prescription or pill bottle before printing or using any generated log.</p>
              <p><strong>3. Beta software.</strong> PillPaper is currently in beta. It may contain bugs or inaccuracies. Use it at your own discretion and always cross-reference with your healthcare provider's instructions.</p>
              <p><strong>4. No warranties.</strong> PillPaper is provided "as is" without warranties of any kind. The developer is not liable for errors, omissions, or any harm resulting from use of the application.</p>
              <p><strong>5. Your responsibility.</strong> You are solely responsible for the accuracy of any information you enter or print. PillPaper is a tool to assist you — not to replace clinical judgment.</p>
              <p><strong>6. Intellectual property.</strong> PillPaper™ is a trademark of its developer. The app, design, and content are protected by intellectual property law. You may not reproduce or redistribute the application without permission.</p>
              <p><strong>7. Changes.</strong> These terms may be updated at any time. Continued use of PillPaper constitutes acceptance of any revised terms.</p>
            </div>
            <button onClick={() => setShowTerms(false)} style={{width:"100%",padding:"9px",marginTop:16,fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>Close</button>
          </div>
        </div>
      )}

      {/* ── FEEDBACK MODAL ── */}
      {showFeedback && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:480,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            {fbStatus === "sent" ? (
              <div style={{textAlign:"center",padding:"1rem 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>🎉</div>
                <div style={{fontSize:16,fontWeight:700,color:"#111",marginBottom:6}}>Thanks for the feedback!</div>
                <div style={{fontSize:13,color:"#555",marginBottom:20}}>It goes directly to the developer. Every report helps make PillPaper better.</div>
                <button onClick={() => { setShowFeedback(false); setFbStatus("idle"); setFbForm({what:"",wrong:"",device:"",email:""}); }}
                  style={{padding:"9px 28px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>Close</button>
              </div>
            ) : (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div style={{fontSize:16,fontWeight:700,color:"#111"}}>💬 Report an issue or share an idea</div>
                  <button onClick={() => { setShowFeedback(false); setFbStatus("idle"); }} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#888"}}>✕</button>
                </div>
                <div style={{marginBottom:10}}>
                  {lbl("What were you trying to do?")}
                  <textarea value={fbForm.what} onChange={e => setFbForm(p=>({...p,what:e.target.value}))}
                    placeholder="Adding a medication, downloading the PDF..." rows={2}
                    style={{width:"100%",boxSizing:"border-box",padding:"7px 9px",border:"1px solid #ccc",borderRadius:6,fontSize:13,fontFamily:"inherit",resize:"vertical"}}/>
                </div>
                <div style={{marginBottom:10}}>
                  {lbl("What went wrong — or what's your idea?")}
                  <textarea value={fbForm.wrong} onChange={e => setFbForm(p=>({...p,wrong:e.target.value}))}
                    placeholder="Describe the bug or suggestion..." rows={3}
                    style={{width:"100%",boxSizing:"border-box",padding:"7px 9px",border:"1px solid #ccc",borderRadius:6,fontSize:13,fontFamily:"inherit",resize:"vertical"}}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                  <div>
                    {lbl("Device / browser (optional)")}
                    <input value={fbForm.device} onChange={e => setFbForm(p=>({...p,device:e.target.value}))}
                      placeholder="iPhone, Chrome on Windows..."
                      style={{width:"100%",boxSizing:"border-box",padding:"7px 9px",border:"1px solid #ccc",borderRadius:6,fontSize:13}}/>
                  </div>
                  <div>
                    {lbl("Your email (optional)")}
                    <input value={fbForm.email} onChange={e => setFbForm(p=>({...p,email:e.target.value}))}
                      placeholder="So I can follow up"
                      style={{width:"100%",boxSizing:"border-box",padding:"7px 9px",border:"1px solid #ccc",borderRadius:6,fontSize:13}}/>
                  </div>
                </div>
                {fbStatus === "error" && <div style={{fontSize:12,color:"#dc2626",marginBottom:10}}>Something went wrong — try again or email hello@pillpaper.com</div>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={() => { setShowFeedback(false); setFbStatus("idle"); }}
                    style={{flex:1,padding:"9px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#666",cursor:"pointer"}}>Cancel</button>
                  <button onClick={submitFeedback} disabled={fbStatus==="sending"}
                    style={{flex:2,padding:"9px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>
                    {fbStatus==="sending" ? "Sending…" : "Send Feedback"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* ── SHARE FEEDBACK MODAL ── */}
      {showShareFeedback && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:480,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            {shareFeedbackStatus === "sent" ? (
              <div style={{textAlign:"center",padding:"1rem 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>✨</div>
                <div style={{fontSize:16,fontWeight:700,color:"#111",marginBottom:6}}>Thank you for sharing!</div>
                <div style={{fontSize:13,color:"#555",marginBottom:20}}>Your feedback helps us improve PillPaper for everyone.</div>
                <button onClick={() => { setShowShareFeedback(false); setShareFeedbackStatus("idle"); setShareFeedbackForm({feedback:"",name:"",role:""}); }}
                  style={{padding:"9px 28px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>Close</button>
              </div>
            ) : (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div style={{fontSize:16,fontWeight:700,color:"#111"}}>💬 Share Your Experience</div>
                  <button onClick={() => { setShowShareFeedback(false); setShareFeedbackStatus("idle"); }} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#888"}}>✕</button>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:"#374151",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Your Feedback</label>
                  <textarea value={shareFeedbackForm.feedback} onChange={e => setShareFeedbackForm(p=>({...p,feedback:e.target.value}))}
                    placeholder="Tell us what you think about PillPaper..."
                    rows={4}
                    style={{width:"100%",boxSizing:"border-box",padding:"9px 11px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,fontFamily:"inherit",resize:"vertical"}}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                  <div>
                    <label style={{display:"block",fontSize:11,fontWeight:600,color:"#374151",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Your Name (optional)</label>
                    <input value={shareFeedbackForm.name} onChange={e => setShareFeedbackForm(p=>({...p,name:e.target.value}))}
                      placeholder="Anonymous"
                      style={{width:"100%",boxSizing:"border-box",padding:"9px 11px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13}}/>
                  </div>
                  <div>
                    <label style={{display:"block",fontSize:11,fontWeight:600,color:"#374151",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Role (optional)</label>
                    <input value={shareFeedbackForm.role} onChange={e => setShareFeedbackForm(p=>({...p,role:e.target.value}))}
                      placeholder="e.g. Caregiver, RN"
                      style={{width:"100%",boxSizing:"border-box",padding:"9px 11px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13}}/>
                  </div>
                </div>
                <div style={{fontSize:11,color:"#6b7280",marginBottom:16,lineHeight:1.5}}>
                  We may share your feedback publicly to help others learn about PillPaper. Your email will never be shared.
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={() => { setShowShareFeedback(false); setShareFeedbackStatus("idle"); }}
                    style={{flex:1,padding:"9px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#666",cursor:"pointer"}}>Cancel</button>
                  <button onClick={submitShareFeedback} disabled={shareFeedbackStatus==="sending" || !shareFeedbackForm.feedback.trim()}
                    style={{flex:2,padding:"9px",fontSize:13,fontWeight:600,background:shareFeedbackForm.feedback.trim()?"#2563eb":"#d1d5db",color:"#fff",border:"none",borderRadius:8,cursor:shareFeedbackForm.feedback.trim()?"pointer":"not-allowed"}}>
                    {shareFeedbackStatus==="sending" ? "Sending…" : "Share Feedback"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,padding:"10px 14px",marginBottom:"1rem",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",position:"sticky",top:8,zIndex:100,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:16}}>🧪</div>
        <div style={{flex:1,minWidth:200}}>
          <span style={{fontWeight:700,color:"#92400e",fontSize:13}}>PillPaper is currently in beta. </span>
          <span style={{fontSize:12,color:"#78350f"}}>You may encounter bugs or unexpected behavior. Always verify your medication information with your pharmacist or prescriber.</span>
        </div>
        <a onClick={() => setShowFeedback(true)} style={{fontSize:12,fontWeight:600,color:"#92400e",background:"#fef3c7",border:"1px solid #fcd34d",borderRadius:6,padding:"5px 12px",textDecoration:"none",whiteSpace:"nowrap",cursor:"pointer"}}>
          💬 Report an issue
        </a>
      </div>
      
      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "PillPaper",
          "alternateName": "PillPaper™ Medication Tracker",
          "url": "https://pillpaper.com",
          "description": "Free printable medication tracker for families, caregivers, and pet owners. Simple paper MAR (Medication Administration Record) - no app, no login, no data stored.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "PillPaper"
          },
          "featureList": [
            "Printable medication tracker",
            "AI-powered medication label scanning",
            "Weekly medication logs",
            "Support for people and pets",
            "No account required",
            "No data storage",
            "Free forever"
          ]
        })}
      </script>
      
      <style>{`
        @media print {
          @page { size: 11in 8.5in landscape; margin: 0.35in; }
          body * { visibility: hidden; }
          #ppz, #ppz * { visibility: visible !important; }
          #ppz {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            background: white !important;
          }
          .week-sheet {
            display: block !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          .week-sheet:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
        }
      `}</style>

      <div id="ppz" style={showPrint
        ? {display:"block"}
        : {position:"absolute",left:"-9999px",top:0,width:"1056px",pointerEvents:"none",zIndex:-1}}>
        {weeks.slice(0,printWeeks).map((wk,wi) => <Sheet key={wi} patient={patient} dates={wk} meds={namedM} weekNum={wi+1} fz={fz} vitals={vitals} sortAlpha={sortAlpha}/>)}
      </div>

      {/* Combined Print Confirmation + Week Selection Modal */}
      {showPrintConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:480,boxShadow:"0 8px 32px rgba(0,0,0,0.18)",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontSize:18,fontWeight:700,marginBottom:14,color:"#111"}}>📋 Print Settings</div>

            {/* Sort order */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#111"}}>Medication sort order:</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[[false,"🕐 By time of day","Daytime, then PRN"],[true,"🔤 Alphabetical","A–Z, all meds together"]].map(([val,label,sub]) => (
                  <button key={String(val)} onClick={() => setSortAlpha(val)}
                    style={{padding:"10px 8px",fontSize:12,fontWeight:sortAlpha===val?600:400,border:sortAlpha===val?"2px solid #2563eb":"1px solid #ddd",borderRadius:8,background:sortAlpha===val?"#eff6ff":"#fff",color:sortAlpha===val?"#2563eb":"#666",cursor:"pointer",textAlign:"left"}}>
                    <div>{label}</div>
                    <div style={{fontSize:10,color:sortAlpha===val?"#3b82f6":"#999",marginTop:2}}>{sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size Selector */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#111"}}>Print size:</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                {[["compact","Compact"],["normal","Normal"],["large","Large"],["xlarge","Extra Large"]].map(([s,label]) => (
                  <button key={s} onClick={() => {
                    setFzKey(s);
                    if(s === "compact" && (selectedWeeks === 1 || selectedWeeks === 3 || selectedWeeks === "2p")) setSelectedWeeks(2);
                    if(s !== "compact" && selectedWeeks === "2p") setSelectedWeeks(2);
                  }}
                    style={{padding:"10px 6px",fontSize:12,fontWeight:fzKey===s?600:400,
                      border:fzKey===s?"2px solid #2563eb":"1px solid #ddd",borderRadius:8,
                      background:fzKey===s?"#eff6ff":"#fff",color:fzKey===s?"#2563eb":"#666",cursor:"pointer"}}>
                    {label}
                  </button>
                ))}
              </div>

            </div>

            {/* Weeks to print */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#111"}}>Weeks to print:</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {(fzKey === "compact" ? [2,4] : [1,2,3,4]).map(w => (
                  <button key={w} onClick={() => setSelectedWeeks(w)}
                    style={{padding:"12px 8px",fontSize:14,fontWeight:selectedWeeks===w?700:400,
                      border:selectedWeeks===w?"2px solid #2563eb":"1px solid #ddd",
                      borderRadius:10,background:selectedWeeks===w?"#eff6ff":"#fff",
                      color:selectedWeeks===w?"#2563eb":"#555",cursor:"pointer"}}>
                    {w} weeks
                  </button>
                ))}
              </div>
            </div>

            {/* Start date */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#111"}}>Start date:</div>
              <input type="date" value={start} onChange={e => setStart(e.target.value)}
                style={{width:"100%",boxSizing:"border-box",padding:"8px 10px",fontSize:13,border:"1px solid #ddd",borderRadius:8}}/>
            </div>

            {/* Disclaimer */}
            <div style={{background:"#fffbf0",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:11,color:"#78350f",lineHeight:1.5}}>
              By clicking "I Agree" you confirm that you will verify the printed record against your original prescriptions or pill bottles before use, and that PillPaper is not responsible for medication errors. This log is for personal tracking only and is not a substitute for medical advice.
            </div>

            <button onClick={() => { if(fzKey === "compact") { doActionCompact2Week(typeof selectedWeeks === "number" ? selectedWeeks : 2); } else { doAction(selectedWeeks); setShowPrintConfirm(false); } }}
              style={{width:"100%",padding:"14px",fontSize:15,fontWeight:700,background:"#1a9e50",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",marginBottom:8,boxShadow:"0 4px 12px rgba(26,158,80,0.28)"}}>
              ✅ I Agree — Generate My Log
            </button>
            <button onClick={() => setShowPrintConfirm(false)} style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── iOS ADD TO HOME SCREEN HINT ── */}
      {showIosHint && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:2000,padding:"1rem",background:"rgba(0,0,0,0.4)"}}>
          <div style={{background:"#fff",borderRadius:16,padding:"1.25rem 1.25rem 1rem",boxShadow:"0 -4px 24px rgba(0,0,0,0.2)",position:"relative"}}>
            <button onClick={() => setShowIosHint(false)}
              style={{position:"absolute",top:10,right:12,background:"none",border:"none",fontSize:18,color:"#999",cursor:"pointer"}}>✕</button>
            <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12}}>
              <img src="/phil.png" alt="Phil" style={{width:44,height:44,objectFit:"contain",flexShrink:0}}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:2}}>Add PillPaper to your Home Screen</div>
                <div style={{fontSize:12,color:"#555",lineHeight:1.5}}>Get quick access to PillPaper — no App Store needed.</div>
              </div>
            </div>
            <div style={{background:"#f0f6ff",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#1e3a5f",lineHeight:1.7}}>
              <div>1. Tap the <strong>Share button</strong> <span style={{fontSize:14}}>⎙</span> at the bottom of Safari</div>
              <div>2. Scroll down and tap <strong>"Add to Home Screen"</strong></div>
              <div>3. Tap <strong>Add</strong> — done!</div>
            </div>
            <button onClick={() => setShowIosHint(false)}
              style={{width:"100%",marginTop:10,padding:"10px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── MED LIST MODAL ── */}
      {showMedListModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:420,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:17,fontWeight:700,color:"#111",marginBottom:4}}>📋 Medication List</div>
            <div style={{fontSize:13,color:"#888",marginBottom:16}}>Choose a format for your medication list</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
              <button onClick={() => { setShowMedListModal(false); setShowMedListText(true); }}
                style={{padding:"14px",fontSize:14,fontWeight:600,background:"#eff6ff",color:"#2563eb",border:"1.5px dashed #2563eb",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>📋</span>
                <div>
                  <div>View &amp; copy text</div>
                  <div style={{fontSize:11,fontWeight:400,color:"#555",marginTop:2}}>Plain text — paste into any system or form</div>
                </div>
              </button>
              <div style={{border:"1.5px solid #2563eb",borderRadius:10,padding:"14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                  <span style={{fontSize:22}}>📄</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,color:"#2563eb"}}>Print medication list PDF</div>
                    <div style={{fontSize:11,color:"#555",marginTop:2}}>Doctor visits, ER, home reference, wallet card</div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6,marginBottom:10}}>
                  {[["compact","Compact"],["normal","Normal"],["large","Large"],["xlarge","Extra Large"]].map(([s,label]) => (
                    <button key={s} onClick={() => setMedListFzKey(s)}
                      style={{padding:"8px 4px",fontSize:12,fontWeight:medListFzKey===s?700:400,
                        border:medListFzKey===s?"2px solid #2563eb":"1px solid #ddd",borderRadius:8,
                        background:medListFzKey===s?"#eff6ff":"#fff",color:medListFzKey===s?"#2563eb":"#666",cursor:"pointer"}}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{fontSize:11,color:"#78350f",background:"#fffbf0",border:"1px solid #fde68a",borderRadius:6,padding:"8px 10px",marginBottom:8,lineHeight:1.5}}>
                  By clicking "I Agree" you confirm you will verify this list against your original prescriptions before use. For personal reference only — not a substitute for medical advice.
                </div>
                <button onClick={() => { setShowMedListModal(false); generateMedListPDF(medListFzKey); }}
                  style={{width:"100%",padding:"10px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>
                  ✅ I Agree — Download PDF
                </button>
              </div>
            </div>
            <button onClick={() => setShowMedListModal(false)} style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── MED LIST TEXT VIEW MODAL ── */}
      {showMedListText && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:520,boxShadow:"0 8px 32px rgba(0,0,0,0.2)",display:"flex",flexDirection:"column",maxHeight:"80vh"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:16,fontWeight:700,color:"#111"}}>📋 Medication List</div>
              <button onClick={() => setShowMedListText(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#888"}}>✕</button>
            </div>
            <div style={{fontSize:12,color:"#888",marginBottom:10}}>Select all and copy, or use the button below</div>
            <textarea
              readOnly
              value={buildMedListText()}
              style={{flex:1,width:"100%",boxSizing:"border-box",minHeight:300,padding:"10px",fontSize:12,
                border:"1px solid #ddd",borderRadius:8,background:"#f8f9fa",color:"#111",
                fontFamily:"monospace",resize:"none",lineHeight:1.6}}
              onFocus={e => e.target.select()}
            />
            <div style={{display:"flex",gap:8,marginTop:12}}>
              <button onClick={() => {
                const text = buildMedListText();
                navigator.clipboard?.writeText(text).catch(() => {});
                setMedListCopied(true);
                setTimeout(() => setMedListCopied(false), 3000);
              }} style={{flex:2,padding:"11px",fontSize:14,fontWeight:600,background:medListCopied?"#1a9e50":"#2563eb",color:"#fff",border:"none",borderRadius:8,cursor:"pointer"}}>
                {medListCopied ? "✓ Copied!" : "📋 Copy to Clipboard"}
              </button>
              <button onClick={() => { setShowMedListText(false); setMedListCopied(false); }}
                style={{flex:1,padding:"11px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#666",cursor:"pointer"}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REMOVAL TOAST ── */}
      {removeMsg && (
        <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:3000,
          background:"#fff",border:"2px solid #dc2626",color:"#dc2626",
          padding:"20px 36px",borderRadius:16,
          fontSize:16,fontWeight:700,boxShadow:"0 8px 32px rgba(0,0,0,0.25)",
          whiteSpace:"nowrap",pointerEvents:"none",textAlign:"center"}}>
          {removeMsg}
        </div>
      )}

      {/* ── BULK BUSY OVERLAY ── */}
      {bulkBusy && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"2rem 2.5rem",textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:32,marginBottom:10}}>⏳</div>
            <div style={{fontSize:15,fontWeight:600,color:"#f59e0b",marginBottom:4}}>Reading list, please wait…</div>
            <div style={{fontSize:12,color:"#888"}}>This may take a few seconds</div>
          </div>
        </div>
      )}

      {/* ── BULK LIST PHOTO MODAL ── */}
      {showBulkListModal && bulkFile1 && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={(e) => { if(e.target===e.currentTarget) setShowBulkListModal(false); }}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"1.5rem 1.5rem 2rem",width:"100%",maxWidth:480,boxShadow:"0 -8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{width:40,height:4,background:"#e5e5e5",borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <span style={{fontSize:28}}>📄</span>
              <div style={{fontSize:18,fontWeight:700,color:"#111"}}>Page 1 captured!</div>
            </div>
            <div style={{fontSize:13,color:"#666",marginBottom:16,lineHeight:1.5}}>
              Is this a <strong>multi-page list?</strong> Take a photo of the next page, or read this page now.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
              <button onClick={() => {
                const el = bulkImgRef2.current;
                if(el){el.value="";el.click();}
              }} style={{padding:"14px",fontSize:14,fontWeight:600,background:"#1a9e50",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>📷</span>
                <div>
                  <div>Take Photo of Page 2</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Second page of the same document</div>
                </div>
              </button>
              <button onClick={() => {
                setShowBulkListModal(false);
                parseBulkImage(bulkFile1, bulkFile2);
              }} style={{padding:"14px",fontSize:14,fontWeight:600,background:"#fff",color:"#1a9e50",border:"1.5px dotted #22c55e",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>✅</span>
                <div>
                  <div>Done — Add to My List</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.7,color:"#555"}}>Use just this one page</div>
                </div>
              </button>
            </div>
            <button onClick={() => { setShowBulkListModal(false); setBulkFile1(null); setBulkFile2(null); }}
              style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── PENDING PHOTO MODAL ── */}
      {pendingPhotoModal && pendingPhotos[pendingPhotoModal.medIndex] && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={(e) => { if(e.target===e.currentTarget) setPendingPhotoModal(null); }}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"1.5rem 1.5rem 2rem",width:"100%",maxWidth:480,boxShadow:"0 -8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{width:40,height:4,background:"#e5e5e5",borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <img src="/philcamera.png" alt="Phil" style={{width:44,height:44,objectFit:"contain",flexShrink:0}}/>
              <div style={{fontSize:18,fontWeight:700,color:"#111"}}>Photo 1 taken!</div>
            </div>
            <div style={{fontSize:13,color:"#666",marginBottom:16,lineHeight:1.5}}>
              This is still the <strong>same bottle</strong> — flip it over to capture the other side, or read now with just this photo.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
              <button onClick={() => {
                const el = fileRefs2.current[pendingPhotoModal.medIndex];
                if(el){el.value="";el.click();}
                setPendingPhotoModal(null);
              }} style={{padding:"14px",fontSize:14,fontWeight:600,background:"#1a9e50",color:"#fff",border:"2px solid #1a9e50",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>📷</span>
                <div>
                  <div>📷 Flip &amp; Take Photo 2</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Other side of this same bottle</div>
                </div>
              </button>
              <button onClick={() => {
                const i = pendingPhotoModal.medIndex;
                const pending = pendingPhotos[i];
                parseImage(i, pending.file);
                setPendingPhotos(prev => { const n={...prev}; delete n[i]; return n; });
                setPendingPhotoModal(null);
              }} style={{padding:"14px",fontSize:14,fontWeight:600,background:"#fff",color:"#1a9e50",border:"1.5px dotted #22c55e",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>✅</span>
                <div>
                  <div>Done with this bottle — Add to my List</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.7,color:"#555"}}>Use just this one photo</div>
                </div>
              </button>
            </div>
            <button onClick={() => setPendingPhotoModal(null)}
              style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── DELIVERY MODAL ── */}
      {showDelivery && deliveryPdf && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:16,padding:"1.75rem",width:"100%",maxWidth:360,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:18,fontWeight:700,color:"#111",marginBottom:4}}>📄 Your log is ready!</div>
            <div style={{fontSize:13,color:"#666",marginBottom:20,lineHeight:1.5}}>What would you like to do with it?</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              <button onClick={deliverSave}
                style={{padding:"14px",fontSize:14,fontWeight:600,background:"#1a9e50",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>📥</span>
                <div>
                  <div>{/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "Save to my phone" : "Download PDF"}</div>
                  <div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Downloads the PDF directly</div>
                </div>
              </button>
              {/Android/i.test(navigator.userAgent) && (
                <button onClick={() => {
                  const url = URL.createObjectURL(deliveryPdf.blob);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                  setShowDelivery(false);
                }} style={{padding:"14px",fontSize:14,fontWeight:600,background:"#7c3aed",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:22}}>👁️</span>
                  <div>
                    <div>Preview PDF</div>
                    <div style={{fontSize:11,fontWeight:400,opacity:0.85}}>View in browser before saving</div>
                  </div>
                </button>
              )}
              {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
                <button onClick={deliverEmail}
                  style={{padding:"14px",fontSize:14,fontWeight:600,background:"#2563eb",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:22}}>📤</span>
                  <div>
                    <div>Share / Print</div>
                    <div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Email, print, or send to another app</div>
                  </div>
                </button>
              )}
            </div>
            <button onClick={() => setShowDelivery(false)}
              style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Week picker modal */}
      {showWeekPicker && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:"100%",maxWidth:340,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:4,color:"#111"}}>How many weeks?</div>
            <div style={{fontSize:12,color:"#888",marginBottom:16}}>Select how many weeks to include in your PDF.</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[1,2,3,4].map(w => (
                <button key={w} onClick={() => doAction(w)} style={{padding:"14px 8px",fontSize:15,fontWeight:600,border:"1px solid #bfdbfe",borderRadius:10,background:"#eff6ff",color:"#2563eb",cursor:"pointer"}}>
                  {w} week{w>1?"s":""}
                </button>
              ))}
            </div>
            <button onClick={() => setShowWeekPicker(false)} style={{width:"100%",padding:"10px",fontSize:13,border:"1px solid #ddd",borderRadius:8,background:"transparent",color:"#888",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* PDF generating overlay */}
      {pdfBusy && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:14,padding:"2rem 2.5rem",textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:32,marginBottom:10}}>📄</div>
            <div style={{fontSize:15,fontWeight:600,color:"#111",marginBottom:4}}>Generating your PDF…</div>
            <div style={{fontSize:12,color:"#888"}}>This may take a few seconds</div>
          </div>
        </div>
      )}


      {/* ── HERO ── */}
      <div style={{background:"linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)",borderRadius:16,padding:"2rem",marginBottom:"1.5rem",color:"#fff",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-30,right:-30,width:180,height:180,background:"rgba(255,255,255,0.04)",borderRadius:"50%"}}/>
        <div style={{position:"absolute",bottom:-40,left:-20,width:140,height:140,background:"rgba(255,255,255,0.04)",borderRadius:"50%"}}/>
        <div style={{position:"relative",display:"flex",flexWrap:"wrap",alignItems:"center",gap:"1.5rem"}}>
          <div style={{flexShrink:0,display:"flex",justifyContent:"center",width:"100%",maxWidth:200}}>
            <img src="/phil.png" alt="Phil the Pill" style={{width:170,height:170,objectFit:"contain",filter:"drop-shadow(0 6px 16px rgba(0,0,0,0.25))"}}/>
          </div>
          <div style={{flex:1,minWidth:220}}>
            <div style={{fontSize:15,fontWeight:700,color:"#60a5fa",marginBottom:8}}>PILLPAPER™</div>
            
            {/* Headline */}
            <div style={{fontSize:32,fontWeight:800,letterSpacing:-1,marginBottom:8,lineHeight:1.15}}>
              Stop guessing if a dose was already given.
            </div>
            
            {/* Subheadline */}
            <div style={{fontSize:15,marginBottom:12,lineHeight:1.5,color:"rgba(255,255,255,0.95)"}}>
              Snap a photo of your medications or type them in — PillPaper builds a printable tracker in seconds.
            </div>
            
            {/* Power line */}
            <div style={{fontSize:14,marginBottom:20,lineHeight:1.5,color:"#fff",fontWeight:600}}>
              Next week? Just snap a photo of your tracker. No re-entry required.
            </div>
            
            {/* CTA */}
            <div style={{marginBottom:12}}>
              <button onClick={() => document.getElementById("app-start").scrollIntoView({behavior:"smooth"})}
                style={{padding:"14px 32px",fontSize:16,fontWeight:700,background:"#fff",color:"#2563eb",border:"none",borderRadius:50,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,0,0,0.2)",marginBottom:6}}>
                Create My Medication Log →
              </button>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",textAlign:"center"}}>Built and tested by a nurse and clinical systems analyst</div>
            </div>
            
            {/* Benefits line */}
            <div style={{display:"flex",gap:8,fontSize:11,opacity:0.75,flexWrap:"wrap",marginBottom:16,alignItems:"center"}}>
              <span>✓ No login</span><span style={{opacity:0.4}}>·</span>
              <span>✓ No data stored</span><span style={{opacity:0.4}}>·</span>
              <span>✓ Free</span><span style={{opacity:0.4}}>·</span>
              <span>🔒 Patent Pending</span>
            </div>
          </div>
        </div>
      </div>

      {/* Visual flow - moved outside hero */}
      <div style={{background:"#f8faff",border:"1px solid #dbeafe",borderRadius:12,padding:"16px",marginBottom:"1.5rem",textAlign:"center"}}>
        <div style={{fontSize:14,color:"#1e3a5f",marginBottom:6,fontWeight:600}}>
          📷 Snap → 🧾 Print → ✍️ Write → 🔁 Repeat
        </div>
        <div style={{fontSize:12,color:"#6b7280",fontWeight:600}}>
          Build it once. Reuse it forever.
        </div>
      </div>

      {/* ── PROBLEM ── */}
      <div style={{background:"#f8faff",border:"1px solid #dbeafe",borderRadius:12,padding:"1rem 1.25rem",marginBottom:"1.5rem"}}>
        <div style={{fontSize:13,lineHeight:1.7,color:"#374151",marginBottom:8,textAlign:"center"}}>
          When more than one person is managing medications, no one is ever fully sure:
        </div>
        <div style={{fontSize:18,fontWeight:700,fontStyle:"italic",textAlign:"center",marginBottom:8,color:"#1e3a5f"}}>
          "Did I already take that today?"
        </div>
        <div style={{fontSize:13,lineHeight:1.7,color:"#374151",textAlign:"center",marginBottom:4}}>
          And in an emergency room:
        </div>
        <div style={{fontSize:16,fontWeight:700,fontStyle:"italic",textAlign:"center",marginBottom:8,color:"#1e3a5f"}}>
          "What time was the last dose?"
        </div>
        <div style={{fontSize:12,lineHeight:1.7,color:"#6b7280",textAlign:"center"}}>
          PillPaper makes sure you know the answer.
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{background:"#fff",border:"1px solid #e5e5e5",borderRadius:14,padding:"1.5rem",marginBottom:"1.5rem"}}>
        <div style={{fontSize:18,fontWeight:700,color:"#111",textAlign:"center",marginBottom:16}}>How it works</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          
          {/* Step 1 with label reading and review integrated */}
          <div style={{background:"#f8faff",border:"1px solid #e8f0fe",borderRadius:12,padding:"1rem 1.25rem"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:12}}>
              <div style={{background:"#2563eb",color:"#fff",borderRadius:"50%",width:28,height:28,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>1</div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:4}}>💊 Add your medications</div>
                <div style={{fontSize:12,color:"#666",lineHeight:1.6,marginBottom:12}}>
                  Type them in, take a photo of a pill bottle, or paste from your patient portal
                </div>
                
                {/* Label reading feature highlight */}
                <div style={{display:"flex",gap:12,alignItems:"center",background:"#fff",padding:"12px",borderRadius:8,border:"1px solid #bfdbfe"}}>
                  <div style={{width:48,height:48,borderRadius:"50%",overflow:"hidden",flexShrink:0,border:"2px solid #bfdbfe"}}>
                    <img src="/philcloseup.png" alt="PillPaper mascot" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center center"}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#1e3a5f",marginBottom:2}}>PillPaper reads labels for you</div>
                    <div style={{fontSize:11,color:"#374151",lineHeight:1.5}}>
                      Point your phone at a pill bottle — name, strength, dose, frequency — filled in automatically. Then review for accuracy before printing.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Steps 2-4 (renumbered from 3-5) */}
          {[
            {step:"2", icon:"🖨️", title:"Print and put it on the fridge", desc:"Anyone in the house can read it — no app, no login"},
            {step:"3", icon:"✍️", title:"Record by writing on the paper", desc:"Write the time every time a dose is given — create an exact record"},
            {step:"4", icon:"🔁", title:"Repeat when ready", desc:"Take a photo of your filled-in sheet and PillPaper generates next week's log automatically"},
          ].map(s => (
            <div key={s.step} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"12px 14px",background:"#f8faff",borderRadius:10,border:"1px solid #e8f0fe"}}>
              <div style={{background:"#2563eb",color:"#fff",borderRadius:"50%",width:28,height:28,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{s.step}</div>
              <div style={{fontSize:18,flexShrink:0}}>{s.icon}</div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#111",marginBottom:2}}>{s.title}</div>
                <div style={{fontSize:12,color:"#666"}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── APP START ── */}
      <div id="app-start" style={{marginBottom:"1.5rem",paddingTop:"0.5rem"}}>
        <div style={{background:"linear-gradient(135deg,#1e3a5f,#2563eb)",borderRadius:12,padding:"1rem 1.5rem",display:"flex",alignItems:"center",gap:12,color:"#fff"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:2}}>Let's build your Medication Log</div>
            <div style={{fontSize:12,opacity:0.85}}>Type them in, take a photo of a pill bottle, paste from your paperwork, or snap your previous PillPaper to carry it forward.</div>
          </div>
          <img src="/philcamera.png" alt="Phil" style={{width:56,height:56,objectFit:"contain",flexShrink:0}}/>
        </div>
      </div>

      {/* ── APP HEADER (compact) ── */}
      <div style={{marginBottom:"1rem",display:"flex",alignItems:"center",gap:14,background:"#fff",borderRadius:12,padding:"0.75rem 1.25rem",border:"1.5px solid #1e3a5f"}}>
        <div style={{flex:1}}>
          <div style={{fontSize:17,fontWeight:500,color:"#111"}}>PillPaper™</div>
        </div>
        <button onClick={clear} style={{padding:"8px 14px",fontSize:13,background:confirm?"#fee2e2":"transparent",color:confirm?"#dc2626":"#666",border:"1px solid "+(confirm?"#fca5a5":"#ddd"),borderRadius:"8px",cursor:"pointer",whiteSpace:"nowrap"}}>
          {confirm?"Tap again to confirm":"Clear all & start over"}
        </button>
      </div>

      {/* Bulk import */}
      <div style={{...card, border:"1.5px solid #1e3a5f", borderLeft:"4px solid #1e3a5f", marginBottom:"1rem"}}>
        <div style={{fontSize:17,fontWeight:500,marginBottom:4,color:"#111"}}>Import an entire medication list at once</div>
        <div style={{fontSize:13,color:"#888",marginBottom:14}}>From your patient portal, discharge paperwork, or a printed list</div>

        {/* 2-option card buttons */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
          {[
            {mode:"text", icon:"📋", label:"Type or paste text",    sub:"From a portal or document"},
            {mode:"list", icon:"📷", label:"Photo of a medication list", sub:"Discharge papers, previous PillPaper, or any printed list"},
          ].map(({mode,icon,label,sub}) => {
            const active = bulkMode === mode;
            const isPhoto = mode === "list";
            const activeBorder = isPhoto ? "2px dotted #1a9e50" : "2px dashed #2563eb";
            const activeBg    = isPhoto ? "#dcfce7" : "#eff6ff";
            const activeColor = isPhoto ? "#1a9e50" : "#1d4ed8";
            const activeSubColor = isPhoto ? "#1a9e50" : "#3b82f6";
            const idleBorder  = isPhoto ? "1.5px dotted #22c55e" : "1.5px dashed #bfdbfe";
            const idleBg      = isPhoto ? "#dcfce7" : "#f8faff";
            const idleColor   = isPhoto ? "#1a9e50" : "#1d4ed8";
            return (
              <button key={mode} onClick={() => {
                setBulkMode(mode);
                setBulkImg(mode === "list");
                setBulkMsg("");
                setBulkFile1(null); setBulkFile2(null);
                setTimeout(() => {
                  if(mode === "text") bulkTextareaRef.current?.focus();
                  if(mode === "list") bulkImgRef.current?.click();
                }, 50);
              }} style={{padding:"14px 12px",textAlign:"left",
                border:active?activeBorder:idleBorder,borderRadius:"8px",
                background:active?activeBg:idleBg,cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{fontSize:13,fontWeight:500,color:active?activeColor:idleColor,marginBottom:3}}>{icon} {label}</div>
                <div style={{fontSize:11,color:active?activeSubColor:idleColor,opacity:active?1:0.7}}>{sub}</div>
              </button>
            );
          })}
        </div>

        {bulkMode === "text" && (
          <>
            <textarea
              ref={bulkTextareaRef}
              value={bulk}
              onChange={e => { setBulk(e.target.value); setBulkPastedImg(null); }}
              onPaste={e => {
                // Check if clipboard contains an image
                const items = e.clipboardData?.items;
                if(!items) return;
                for(const item of items) {
                  if(item.type.startsWith("image/")) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if(file) {
                      setBulkPastedImg(file);
                      setBulk("");
                    }
                    return;
                  }
                }
                // Otherwise let normal text paste happen
              }}
              placeholder={bulkPastedImg ? "" : "Type or paste text here — or paste a screenshot with Ctrl+V / Cmd+V"}
              style={{width:"100%",boxSizing:"border-box",height:bulkPastedImg?32:100,padding:"8px 10px",fontSize:12,
                border:"1px solid #ddd",borderRadius:"6px",
                background:"#fafafa",color:"#111",resize:bulkPastedImg?"none":"vertical",fontFamily:"inherit"}}
            />
            {bulkPastedImg && (
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#f8f9fa",border:"1px solid #d1d5db",borderRadius:6,marginTop:6}}>
                <span style={{fontSize:16}}>🖼️</span>
                <div style={{flex:1,fontSize:12,color:"#374151"}}>Screenshot pasted — ready to read</div>
                <button onClick={() => setBulkPastedImg(null)} style={{fontSize:11,color:"#888",background:"none",border:"none",cursor:"pointer",padding:"2px 6px"}}>✕ Remove</button>
              </div>
            )}
            <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
              {(bulk.trim() || bulkPastedImg || bulkBusy) && (
                <button onClick={() => {
                  if(bulkPastedImg) {
                    parseBulkImage(bulkPastedImg);
                  } else {
                    parseBulk();
                  }
                }} disabled={bulkBusy||(!bulk.trim()&&!bulkPastedImg)}
                  style={{padding:"7px 16px",fontSize:13,border:"1.5px dotted #22c55e",borderRadius:"6px",
                    background:bulkBusy?"#f59e0b":"#dcfce7",color:bulkBusy?"#fff":"#1a9e50",
                    cursor:bulkBusy?"not-allowed":"pointer",fontWeight:600}}>
                  {bulkBusy?"⏳ Reading, please wait…":"Read & Add to Medication List ↗"}
                </button>
              )}
              {bulkMsg && (
                <div style={{padding:"10px 14px",borderRadius:8,
                  background:bulkMsg.includes("added")?"#dcfce7":"#fef2f2",
                  border:`1px solid ${bulkMsg.includes("added")?"#22c55e":"#fca5a5"}`,
                  fontSize:13,fontWeight:600,
                  color:bulkMsg.includes("added")?"#1a9e50":"#dc2626"}}>
                  {bulkMsg.includes("added") ? `✅ ${bulkMsg}` : bulkMsg}
                </div>
              )}
            </div>
          </>
        )}

        {bulkMode === "list" && (
          <>
            <input type="file" accept="image/*" capture="environment" ref={bulkImgRef} style={{display:"none"}}
              onChange={e => { if(e.target.files?.[0]) { setBulkFile1(e.target.files[0]); setBulkFile2(null); setBulkMsg(""); setShowBulkListModal(true); } }}/>
            <input type="file" accept="image/*" capture="environment" ref={bulkImgRef2} style={{display:"none"}}
              onChange={e => { if(e.target.files?.[0]) { const f2 = e.target.files[0]; setBulkFile2(f2); setShowBulkListModal(false); parseBulkImage(bulkFile1, f2); } }}/>
            <div style={{textAlign:"center",padding:"0.5rem 0",fontSize:12,color:"#888"}}>Tap "Photo of a medication list" above to get started</div>
            {bulkMsg && (
              <div style={{marginTop:8,padding:"10px 14px",borderRadius:8,
                background:bulkMsg.includes("added")?"#dcfce7":"#fef2f2",
                border:`1px solid ${bulkMsg.includes("added")?"#22c55e":"#fca5a5"}`,
                fontSize:13,fontWeight:600,
                color:bulkMsg.includes("added")?"#1a9e50":"#dc2626",
                textAlign:"center"}}>
                {bulkMsg.includes("added") ? `✅ ${bulkMsg}` : bulkMsg}
              </div>
            )}
          </>
        )}


      </div>

      {/* Medications */}
      <div style={{...card, border:"1.5px solid #1e3a5f", borderLeft:"4px solid #1e3a5f"}}>
        <div style={{fontSize:17,fontWeight:500,marginBottom:4,color:"#111"}}>Add individual medications</div>
        <div style={{fontSize:13,color:"#888",marginBottom:14}}>Type it in or use the "📷 Photo of a Pill Bottle" button on each medication card below</div>

        {meds.some(m => m._aiParsed) && (
          <div style={{background:"#fefce8",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:14,display:"flex",gap:8,alignItems:"center"}}>
            <div style={{fontSize:14,flexShrink:0}}>✓</div>
            <div style={{flex:1,fontSize:11,color:"#78350f",lineHeight:1.5}}>
              <strong>AI-filled — please verify</strong> names, doses, and frequencies against your original bottle or paperwork before printing.
            </div>
          </div>
        )}
        {meds.map((med,i) => {
          const isEmpty = !med.name.trim();
          const borderColor = isEmpty?"#e5e5e5":isPRN(med.freq)?"#a05000":isHS(med.freq)||isEVENING(med.freq)?"#2563eb":"#1a9e50";
          const labelText = isPRN(med.freq)?"PRN / As Needed":isHS(med.freq)?"Bedtime":isEVENING(med.freq)?"Evening / Bedtime":isMORNING(med.freq)?"Morning":"Daytime";
          const isAltOf = meds.some(m => String(m.alternatesWith)===String(med.id));
          return (
            <div key={med.id} className="med-card" style={{border:"1px solid #e5e5e5",borderLeft:"4px solid "+borderColor,borderRadius:"8px",padding:"10px 12px",marginBottom:12,background:"#fafafa"}}>
              <div style={{fontSize:11,fontWeight:600,color:med.freq?borderColor:"#bbb",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>
                {med.freq ? labelText : "New Medication"}
                {isAltOf && <span style={{marginLeft:8,color:"#999",textTransform:"none",fontWeight:400}}>(alternates with another med above)</span>}
              </div>
              
              {/* Duplicate warning */}
              {med._stopMed && (
                <div style={{marginBottom:10,padding:"10px 12px",background:"#fef2f2",border:"2px solid #dc2626",borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>⛔</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#dc2626",marginBottom:2}}>STOP — Do not take this medication</div>
                    <div style={{fontSize:11,color:"#991b1b"}}>This was listed as a discontinued medication. Remove it or verify with your prescriber.</div>
                  </div>
                  <button onClick={() => { setMeds(prev => prev.filter((_,idx) => idx!==i)); }}
                    style={{fontSize:12,fontWeight:700,padding:"6px 12px",background:"#dc2626",border:"none",borderRadius:6,cursor:"pointer",color:"#fff",whiteSpace:"nowrap"}}>Remove</button>
                </div>
              )}
              {med._duplicateWarning && (
                <div style={{marginBottom:10,padding:"8px 10px",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:6,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>⚠️</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#dc2626",marginBottom:2}}>Possible duplicate</div>
                    <div style={{fontSize:10,color:"#991b1b"}}>{med.name} {med.dose} already appears in your list. Do you need both?</div>
                  </div>
                  <button onClick={() => { const c=[...meds]; c[i]={...c[i],_duplicateWarning:false}; setMeds(c); }}
                    style={{fontSize:10,padding:"3px 8px",background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:4,cursor:"pointer",color:"#dc2626",whiteSpace:"nowrap"}}>Keep it</button>
                  <button onClick={() => { setMeds(prev => prev.filter((_,idx) => idx!==i)); }}
                    style={{fontSize:10,padding:"3px 8px",background:"#dc2626",border:"none",borderRadius:4,cursor:"pointer",color:"#fff",whiteSpace:"nowrap"}}>Remove</button>
                </div>
              )}

              {/* Handwriting detected warning */}
              {med.hasHandwriting && (
                <div style={{marginBottom:10,padding:"8px 10px",background:"#fffbf0",border:"1px solid #fbbf24",borderRadius:6,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>⚠️</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#92400e",marginBottom:2}}>Handwritten changes detected</div>
                    <div style={{fontSize:10,color:"#78350f"}}>Please review this medication for accuracy before printing</div>
                  </div>
                </div>
              )}
              
              {/* Scan button — hidden after any parse */}
              {!med._aiParsed && !med._bulkImported && (
                <div style={{marginBottom:10}}>
                  <div style={{marginBottom:6}}>
                    <input type="file" accept="image/*" capture="environment" ref={el => { fileRefs.current[i]=el; }} style={{display:"none"}} onChange={e => {
                      if(!e.target.files || !e.target.files[0]) return;
                      const file = e.target.files[0];
                      e.target.value="";
                      checkLandscapeWarning(file, () => {
                        setPendingPhotos(prev => ({...prev, [i]: {file, preview: "(first photo taken)"}}));
                        setPendingPhotoModal({medIndex: i, medName: meds[i].name || "this medication"});
                      });
                    }}/>
                    <input type="file" accept="image/*" capture="environment" ref={el => { fileRefs2.current[i]=el; }} style={{display:"none"}} onChange={e => {
                      if(!e.target.files || !e.target.files[0]) return;
                      const file2 = e.target.files[0];
                      e.target.value="";
                      const pending = pendingPhotos[i];
                      if(pending) {
                        parseImage(i, pending.file, file2);
                        setPendingPhotos(prev => { const n={...prev}; delete n[i]; return n; });
                        setPendingPhotoModal(null);
                      }
                    }}/>
                    <button onClick={() => { const el=fileRefs.current[i]; if(el){el.value="";el.click();} }} disabled={aiIdx===i || bulkBusy}
                      style={{width:"100%",padding:"10px",fontSize:13,
                        border:aiIdx===i?"2px solid #f59e0b":bulkBusy?"1.5px dotted #ccc":"1.5px dotted #22c55e",
                        borderRadius:"6px",
                        background:aiIdx===i?"#f59e0b":bulkBusy?"#f5f5f5":"#dcfce7",
                        color:aiIdx===i?"#fff":bulkBusy?"#999":"#1a9e50",
                        cursor:(aiIdx===i||bulkBusy)?"not-allowed":"pointer",fontWeight:600,textAlign:"center"}}>
                      {aiIdx===i ? "⏳ Reading bottle, please wait…" : bulkBusy ? "⏳ Import in progress…" : "📷 Photo of a Pill Bottle"}
                    </button>
                  </div>
                  {pendingPhotos[i] && (
                    <div style={{display:"flex",gap:6,alignItems:"center",padding:"8px 10px",background:"#dcfce7",border:"1px solid #22c55e",borderRadius:6,cursor:"pointer"}}
                      onClick={() => setPendingPhotoModal({medIndex: i, medName: meds[i].name || "this medication"})}>
                      <span style={{fontSize:14}}>📷</span>
                      <span style={{fontSize:11,color:"#166534",flex:1,fontWeight:600}}>Photo 1 ready — tap to continue</span>
                      <span style={{fontSize:11,color:"#1a9e50"}}>▶</span>
                    </div>
                  )}
                </div>
              )}
              {/* Re-scan button — only shown for bottle-scanned meds, not bulk imports */}
              {med._aiParsed && !med._bulkImported && (
                <div style={{marginBottom:10,display:"flex",gap:6}}>
                  <button onClick={() => {
                    const c=[...meds]; c[i]={...c[i],_aiParsed:false}; setMeds(c);
                    setPendingPhotos(prev=>{ const n={...prev}; delete n[i]; return n; });
                    setTimeout(() => { const el=fileRefs.current[i]; if(el){el.value="";el.click();} }, 100);
                  }} style={{padding:"5px 12px",fontSize:11,color:"#2563eb",background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:6,cursor:"pointer"}}>📷 Retake Photo</button>
                </div>
              )}
              {/* Row 1: Name + Brand (brand only when AI-parsed) */}
              <div style={{display:"grid",gridTemplateColumns:isMobileLayout?"1fr":med._aiParsed?"2fr 1fr":"1fr",gap:"10px 12px",marginBottom:10}}>
                <div>
                  {lbl(med._aiParsed ? "Medication name (generic)" : "Medication name")}
                  <input {...inp({})} value={med.name} onChange={e => setMed(i,"name",e.target.value)} placeholder=""/>
                </div>
                {med._aiParsed && (
                  <div>
                    {lbl("Brand name")}
                    <input {...inp({})} value={med.brand||""} onChange={e => setMed(i,"brand",e.target.value)} placeholder=""/>
                  </div>
                )}
              </div>

              {/* Row 2: Strength · How much per dose · Frequency */}
              <div style={{display:"grid",gridTemplateColumns:isMobileLayout?"1fr":"1fr 1fr 1fr",gap:"10px 12px",marginBottom:10}}>
                <div>
                  {lbl("Strength")}
                  <input {...inp({})} value={med.dose} onChange={e => setMed(i,"dose",e.target.value)} placeholder=""/>
                  <div style={{fontSize:10,color:"#999",marginTop:2}}>e.g. 10mg, 500mg, 2%</div>
                </div>
                <div>
                  {lbl("How much per dose")}
                  <input {...inp({})} value={med.qty||""} onChange={e => setMed(i,"qty",e.target.value)} placeholder=""/>
                  <div style={{fontSize:10,color:"#999",marginTop:2}}>e.g. 1 tablet, 2 capsules, 1 puff</div>
                </div>
                <div>
                  {lbl("Frequency")}
                  <select {...inp({})} value={med.freq} onChange={e => setMed(i,"freq",e.target.value)}>
                    <option value="">— Select —</option>
                    {Object.entries(FREQ_BY_GROUP).map(([g,opts]) => (
                      <optgroup key={g} label={g}>{opts.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}</optgroup>
                    ))}
                  </select>
                  {isPRNWithoutInterval(med.freq) && (
                    <div style={{marginTop:4,fontSize:10,color:"#dc2626",fontWeight:600}}>
                      ⚠️ PRN medications should have a dosing interval — please select one from the dropdown
                    </div>
                  )}
                  {med._isNutrition && !med.freq && (
                    <div style={{marginTop:4,fontSize:10,color:"#b45309",fontWeight:600,fontStyle:"italic"}}>
                      💡 Enter feeding frequency from your doctor's order
                    </div>
                  )}
                </div>
              </div>

              {/* Frequency sub-fields aligned under Frequency (3rd column) */}
              {(med.freq===OTHER_FREQ || isInterval(med.freq)) && (
                <div style={{display:"grid",gridTemplateColumns:isMobileLayout?"1fr":"1fr 1fr 1fr",gap:"0 12px",marginBottom:10}}>
                  {!isMobileLayout && <div/>}{!isMobileLayout && <div/>}
                  <div>
                    {med.freq===OTHER_FREQ && (
                      <div>
                        <input {...inp({style:{border:"1px solid #f0a500",background:"#fffbf0"}})} value={med.customFreq||""} onChange={e => setMed(i,"customFreq",e.target.value)} placeholder=""/>
                        <div style={{fontSize:10,color:"#999",marginTop:2}}>e.g. every 72 hours</div>
                        {!/^(as\s*directed|see\s*instructions?)$/i.test(med.customFreq||"") && <div style={{marginTop:6,background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:6,padding:"6px 10px",display:"flex",gap:6,alignItems:"flex-start"}}>
                          <div style={{fontSize:14,flexShrink:0}}>⚠️</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:11,fontWeight:700,color:"#92400e"}}>Frequency not found</div>
                            <div style={{fontSize:11,color:"#78350f",lineHeight:1.4,marginBottom:4}}>"{med.customFreq || med.freq}" wasn't recognized. Please select the correct frequency from the dropdown or type it in.</div>
                            <button onClick={() => {
                              setFbForm({what:"Unrecognized frequency flagged by user", wrong:`Frequency not in dropdown: "${med.customFreq || med.freq}" — for medication: ${med.name}. Please consider adding this to the frequency list.`, device:"", email:""});
                              setShowFeedback(true);
                            }} style={{fontSize:10,fontWeight:600,color:"#92400e",background:"#fef3c7",border:"1px solid #fcd34d",borderRadius:4,padding:"3px 8px",cursor:"pointer"}}>
                              📬 Flag this to the developer
                            </button>
                          </div>
                        </div>}
                      </div>
                    )}
                    {isInterval(med.freq) && med.freq !== "Once monthly" && (
                      <div>
                        <div style={{fontSize:11,marginBottom:4,
                          color:(() => { const max=med.freq==="Once weekly"?1:med.freq==="Twice weekly"?2:med.freq==="Three times weekly"?3:7; return (med.scheduledDays||[]).length < max ? "#dc2626" : "#666"; })(),
                          fontWeight:(() => { const max=med.freq==="Once weekly"?1:med.freq==="Twice weekly"?2:med.freq==="Three times weekly"?3:7; return (med.scheduledDays||[]).length < max ? 700 : 400; })()}}>
                          {med.freq==="Once weekly"?"Which day? (pick 1)":
                           med.freq==="Twice weekly"?"Which days? (pick 2)":
                           med.freq==="Three times weekly"?"Which days? (pick 3)":
                           "Which days?"}
                        </div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(day => {
                            const selected = (med.scheduledDays||[]).includes(day);
                            const maxSel = med.freq==="Once weekly"?1:med.freq==="Twice weekly"?2:med.freq==="Three times weekly"?3:7;
                            const atMax = (med.scheduledDays||[]).length >= maxSel && !selected;
                            return (
                              <button key={day} onClick={() => {
                                if(atMax) return;
                                const c=[...meds];
                                const days = c[i].scheduledDays||[];
                                c[i]={...c[i], scheduledDays: selected ? days.filter(d=>d!==day) : [...days,day]};
                                setMeds(c);
                              }} style={{padding:"8px 12px",fontSize:13,fontWeight:700,
                                border: selected?"2px solid #2563eb":"2px solid #ddd",
                                borderRadius:"8px",
                                background:selected?"#2563eb":atMax?"#f5f5f5":"#fff",
                                color:selected?"#fff":atMax?"#ccc":"#444",
                                cursor:atMax?"not-allowed":"pointer",
                                boxShadow:selected?"0 2px 6px rgba(37,99,235,0.3)":"none",
                                transition:"all 0.1s"}}>
                                {day}
                              </button>
                            );
                          })}
                        </div>
                        {(() => {
                          const maxSel = med.freq==="Once weekly"?1:med.freq==="Twice weekly"?2:med.freq==="Three times weekly"?3:7;
                          const picked = (med.scheduledDays||[]).length;
                          if(picked >= maxSel) return (
                            <div style={{fontSize:12,color:"#2563eb",fontWeight:600}}>✓ {med.scheduledDays.join(", ")} selected</div>
                          );
                          if(picked > 0) return (
                            <div style={{fontSize:11,color:"#dc2626",fontWeight:600}}>Pick {maxSel - picked} more day{maxSel - picked !== 1 ? "s" : ""}</div>
                          );
                          return (
                            <div style={{fontSize:10,color:"#f59e0b",fontStyle:"italic"}}>💡 If you don't pick specific days, you'll get a "Last dose / Next due" line on the printout</div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Row 3: Reason · Additional notes · Duration */}
              <div style={{display:"grid",gridTemplateColumns:isMobileLayout?"1fr":"1fr 1fr 1fr",gap:"10px 12px",marginBottom:10}}>
                <div>
                  {lbl("Reason for taking (optional)")}
                  <input {...inp({style: isPRN(med.freq) && !med.notes.trim() ? {borderColor:"#f59e0b", background:"#fffbf0"} : {}})} value={med.notes} onChange={e => setMed(i,"notes",e.target.value)} placeholder=""/>
                  {isPRN(med.freq) && !med.notes.trim() && (
                    <div style={{fontSize:10,color:"#b45309",marginTop:3,fontStyle:"italic"}}>💡 Reason is recommended for as-needed medications</div>
                  )}
                </div>
                <div>
                  {lbl("Additional notes (optional)")}
                  <input {...inp({})} value={med.additionalNotes||""} onChange={e => setMed(i,"additionalNotes",e.target.value)} placeholder=""/>
                  <div style={{fontSize:10,color:"#999",marginTop:2}}>e.g. take with food</div>
                </div>
                <div>
                  {lbl("Duration (optional)")}
                  <input {...inp({style:{fontSize:12,padding:"6px 8px"}})} value={med.duration||""} onChange={e => setMed(i,"duration",e.target.value)} placeholder=""/>
                  <div style={{fontSize:10,color:"#999",marginTop:2}}>e.g. 7 days, 14 days</div>
                  {med.duration && (() => {
                    const days = parseInt(med.duration);
                    const isShortCourse = !isNaN(days) && days < 30;
                    return isShortCourse ? (
                      <div style={{marginTop:6}}>
                        {lbl("Date started")}
                        <input {...inp({style:{fontSize:12,padding:"6px 8px"}})} type="date" value={med.dateStarted||""} onChange={e => setMed(i,"dateStarted",e.target.value)}/>
                        {!med.dateStarted && (
                          <div style={{fontSize:10,color:"#f59e0b",marginTop:2,fontStyle:"italic"}}>💡 Add a start date to auto-block days after this med is finished</div>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Bottle info (AI only) */}
              {med._aiParsed && (med.refillsLeft || med.prescriber || med.rxNumber || med.pillDescription) && (
                <div style={{marginBottom:10,padding:"8px 10px",background:"#f8faff",border:"1px solid #dbeafe",borderRadius:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#1e3a5f"}}>📋 Bottle Info</div>
                    <button onClick={() => {
                      const c=[...meds];
                      c[i]={...c[i], pillDescription:"", prescriber:"", rxNumber:"", refillsLeft:""};
                      setMeds(c);
                    }} style={{fontSize:10,color:"#888",background:"none",border:"none",cursor:"pointer",padding:"0 2px"}}>✕ Clear</button>
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    {med.pillDescription && <div style={{fontSize:11,color:"#374151"}}><strong>Pill:</strong> {med.pillDescription}</div>}
                    {med.prescriber && <div style={{fontSize:11,color:"#374151"}}><strong>Prescriber:</strong> {med.prescriber}</div>}
                    {med.rxNumber && <div style={{fontSize:11,color:"#374151"}}><strong>RX#:</strong> {med.rxNumber}</div>}
                    {med.refillsLeft && <div style={{fontSize:11,color:"#374151"}}><strong>Refills left:</strong> {med.refillsLeft}</div>}
                  </div>
                </div>
              )}

              {/* Row 4: Alternates checkbox + Remove button */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <input type="checkbox" id={"alt-"+med.id} checked={!!(med._altChecked||med.alternatesWith)}
                      onChange={e => { const c=[...meds]; c[i]={...c[i],_altChecked:e.target.checked,alternatesWith:e.target.checked?c[i].alternatesWith:null}; setMeds(c); }}/>
                    <label htmlFor={"alt-"+med.id} style={{fontSize:12,color:"#666",cursor:"pointer"}}>Alternates with another med</label>
                  </div>
                  {(med._altChecked||med.alternatesWith) && (
                    <div style={{marginTop:4}}>
                      <select {...inp({style:{marginTop:0}})} value={med.alternatesWith||""} onChange={e => { const c=[...meds]; c[i]={...c[i],alternatesWith:e.target.value||null}; setMeds(c); }}>
                        <option value="">— Select med to alternate with —</option>
                        {meds.filter((_,idx) => idx!==i && _.name && !_.alternatesWith).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <div style={{fontSize:11,color:"#999",marginTop:3}}>Only mark this on one of the two meds — not both.</div>
                    </div>
                  )}
                </div>
                <button onClick={() => {
                  if(!removeConfirm[i]) {
                    setRemoveConfirm(prev => ({...prev,[i]:true}));
                    setTimeout(() => setRemoveConfirm(prev => ({...prev,[i]:false})), 4000);
                  } else {
                    const medName = med.name || "Medication";
                    setRemoving(prev => ({...prev,[i]:true}));
                    setRemoveConfirm(prev => ({...prev,[i]:false}));
                    setTimeout(() => {
                      setMeds(prev => prev.filter((_,idx) => idx!==i));
                      setRemoving(prev => { const n={...prev}; delete n[i]; return n; });
                      setRemoveMsg(`✓ ${medName} removed`);
                      setTimeout(() => setRemoveMsg(""), 3000);
                    }, 600);
                  }
                }} disabled={!!removing[i]} style={{padding:"6px 14px",fontSize:12,border:"1px solid "+(removing[i]?"#fca5a5":removeConfirm[i]?"#fca5a5":"#ddd"),borderRadius:"6px",background:removing[i]?"#fee2e2":removeConfirm[i]?"#fee2e2":"transparent",color:removing[i]?"#dc2626":removeConfirm[i]?"#dc2626":"#888",cursor:removing[i]?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                  {removing[i] ? "Removing…" : removeConfirm[i] ? "Tap again to remove" : "Remove"}
                </button>
              </div>
            </div>
          );
        })}
        <button onClick={() => setMeds([...meds,newMed()])} style={{width:"100%",padding:"12px",fontSize:14,fontWeight:700,border:"2px dashed #1a9e50",borderRadius:"10px",background:"#dcfce7",cursor:"pointer",color:"#1a9e50",marginTop:4}}>＋ Add Another Medication</button>
      </div>

      {/* Custom freq log */}
      <div style={{marginBottom:"1rem"}}>
        <button onClick={async () => { try { const r=await lsGet("marfill_custom_freqs"); setCLog(r?JSON.parse(r.value):[]); } catch(e) { setCLog([]); } setShowLog(true); }} style={{fontSize:12,padding:"5px 12px",border:"1px solid #ddd",borderRadius:"6px",background:"transparent",color:"#888",cursor:"pointer"}}>⚑ Review flagged frequencies</button>
        {showLog && (
          <div style={{marginTop:8,background:"#fffbf0",border:"1px solid #f0a500",borderRadius:"8px",padding:"10px 14px"}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#b07000"}}>Custom frequencies entered</div>
            {cLog.length===0 ? <div style={{fontSize:12,color:"#888"}}>None yet.</div>
              : cLog.map((e,idx) => (
                <div key={idx} style={{fontSize:12,borderBottom:"1px solid #f5d98a",padding:"4px 0",display:"flex",gap:12}}>
                  <span style={{fontWeight:500}}>{e.freq}</span>
                  <span style={{color:"#888"}}>— {e.med||"unknown"}</span>
                  <span style={{color:"#888",marginLeft:"auto"}}>{e.date}</span>
                </div>
              ))}
            <button onClick={() => setShowLog(false)} style={{marginTop:8,fontSize:11,color:"#888",background:"transparent",border:"none",cursor:"pointer"}}>Close</button>
          </div>
        )}
      </div>

      {/* ── READY TO PRINT ── */}
      <div style={{background:"linear-gradient(135deg,#1e3a5f,#2563eb)",borderRadius:12,padding:"1rem 1.5rem",marginBottom:"0.75rem",display:"flex",alignItems:"center",gap:14,color:"#fff"}}>
        <img src="/phil.png" alt="Phil" style={{width:54,height:54,objectFit:"contain",flexShrink:0}}/>
        <div style={{flex:1}}>
          <div style={{fontSize:15,fontWeight:700}}>✅ Last step</div>
          <div style={{fontSize:12,opacity:0.85}}>Review your settings, then hit download.</div>
        </div>
      </div>

      {/* Print Settings + Actions */}
      <div style={{background:"#fff",border:"1px solid #1e3a5f",borderRadius:10,padding:"12px 14px",marginBottom:"0.5rem"}}>
        <div style={{marginBottom:14}}>
          <div>
            {lbl("Name")}
            <input {...inp({style: !patient.trim() ? {borderColor:"#f59e0b", background:"#fffbf0"} : {}})} value={patient} onChange={e => setPatient(e.target.value)} placeholder=""/>
            {!patient.trim() && <div style={{fontSize:10,color:"#b45309",marginTop:3,fontStyle:"italic"}}>💡 Add a name to personalize your log (optional)</div>}
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:600,color:"#111",marginBottom:6}}>Add space for daily vitals?</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              {key:"weight",  label:"⚖️ Daily Weight",  sub:"lbs or kg"},
              {key:"bp",      label:"🩺 Blood Pressure", sub:"mmHg"},
              {key:"bm",      label:"🚽 Bowel Movement",  sub:"daily record"},
            ].map(v => (
              <label key={v.key} onClick={() => setVitals(prev => ({...prev,[v.key]:!prev[v.key]}))}
                style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",border:"1px solid "+(vitals[v.key]?"#2563eb":"#e5e5e5"),borderRadius:8,background:vitals[v.key]?"#eff6ff":"#fafafa",cursor:"pointer",userSelect:"none"}}>
                <input type="checkbox" checked={!!vitals[v.key]} onChange={()=>{}} style={{accentColor:"#2563eb"}}/>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:vitals[v.key]?"#2563eb":"#333"}}>{v.label}</div>
                  <div style={{fontSize:10,color:"#888"}}>{v.sub}</div>
                </div>
              </label>
            ))}
            
            {/* Blood Glucose with frequency dropdown */}
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label onClick={() => setVitals(prev => ({...prev, glucose: prev.glucose ? null : "Three times daily"}))}
                style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",border:"1px solid "+(vitals.glucose?"#2563eb":"#e5e5e5"),borderRadius:8,background:vitals.glucose?"#eff6ff":"#fafafa",cursor:"pointer",userSelect:"none"}}>
                <input type="checkbox" checked={!!vitals.glucose} onChange={()=>{}} style={{accentColor:"#2563eb"}}/>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:vitals.glucose?"#2563eb":"#333"}}>🩸 Blood Glucose</div>
                  <div style={{fontSize:10,color:"#888"}}>mg/dL</div>
                </div>
              </label>
              {vitals.glucose && (
                <select value={vitals.glucose} onChange={e => setVitals(prev => ({...prev, glucose: e.target.value}))}
                  style={{padding:"4px 8px",fontSize:11,border:"1px solid #2563eb",borderRadius:6,background:"#fff",color:"#2563eb",fontWeight:600}}>
                  <option value="Once daily">Once daily</option>
                  <option value="Twice daily">Twice daily</option>
                  <option value="Three times daily">Three times daily</option>
                  <option value="Four times daily">Four times daily</option>
                </select>
              )}
            </div>
          </div>
        </div>
        <div style={{marginTop:10}}>
          <button onClick={print} style={{width:"100%",padding:"18px 10px",fontSize:17,fontWeight:700,background:"#1a9e50",color:"#fff",border:"none",borderRadius:"10px",cursor:"pointer",boxShadow:"0 4px 16px rgba(26,158,80,0.35)"}}>📥 Print Tracking Grid</button>
          <div style={{display:"flex",alignItems:"center",gap:10,marginTop:12,marginBottom:4}}>
            <div style={{flex:1,height:"0.5px",background:"#e5e7eb"}}></div>
            <span style={{fontSize:12,color:"#9ca3af"}}>or</span>
            <div style={{flex:1,height:"0.5px",background:"#e5e7eb"}}></div>
          </div>
          <button onClick={() => setShowMedListModal(true)} style={{width:"100%",padding:"11px 10px",fontSize:14,fontWeight:500,background:"#fff",color:"#2563eb",border:"1.5px solid #2563eb",borderRadius:"10px",cursor:"pointer"}}>📋 Print List Only</button>
        </div>
      </div>
      {saveMsg && <div style={{fontSize:13,color:"#1a9e50",marginBottom:"0.5rem"}}>{saveMsg}</div>}

      {/* Clear button above preview */}
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"0.5rem"}}>
        <button onClick={clear} style={{padding:"10px 22px",fontSize:14,fontWeight:500,background:confirm?"#fee2e2":"#f9fafb",color:confirm?"#dc2626":"#555",border:"1px solid "+(confirm?"#fca5a5":"#d1d5db"),borderRadius:"8px",cursor:"pointer"}}>
          {confirm?"Tap again to confirm":"🗑 Clear all and start over"}
        </button>
      </div>

      {/* Preview — zoomed peek */}
      {namedM.length > 0 && (
        <div style={{border:"1px solid #e5e5e5",borderRadius:"10px",overflow:"hidden",marginBottom:"0.5rem"}}>
          <div style={{background:"#f5f5f5",padding:"8px 14px",fontSize:12,color:"#888",borderBottom:"1px solid #e5e5e5",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Preview</span>
            <span style={{fontSize:11,color:"#bbb"}}>showing first 2 days</span>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",width:"100%",fontSize:11}}>
              <thead>
                <tr style={{background:"#555"}}>
                  <th style={{padding:"5px 8px",color:"#fff",textAlign:"left",fontWeight:600,minWidth:140}}>Medication</th>
                  <th style={{padding:"5px 8px",color:"#fff",textAlign:"center",fontWeight:600,width:60}}>Dose #</th>
                  {weeks[0].slice(0,2).map(d => (
                    <th key={d} style={{padding:"5px 8px",color:"#fff",textAlign:"center",fontWeight:600,width:70}}>
                      {DAYS[d.getDay()]}<br/><span style={{fontWeight:400,fontSize:10}}>{fmtD(d)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...namedM].sort((a,b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).slice(0, showAllPreview ? namedM.length : 4).map((med,mi) => {
                  const when = med.freq === OTHER_FREQ ? med.customFreq : med.freq;
                  const brandPart = med.brand ? ` (${med.brand})` : "";
                  const line1 = `${med.name}${brandPart}${med.dose ? " " + med.dose : ""}`;
                  const totalDoses = med.doses || 1;
                  return Array.from({length:totalDoses}).map((_, di) => (
                    <tr key={`${mi}-${di}`} style={{background:mi%2===0?"#f8faff":"#fff",borderBottom:"1px solid #e5e5e5"}}>
                      {di === 0 && (
                        <td rowSpan={totalDoses} style={{padding:"5px 8px",verticalAlign:"top",borderRight:"1px solid #e5e5e5"}}>
                          <div style={{fontWeight:700,color:"#111",fontSize:11}}>{line1}</div>
                          <div style={{color:"#888",fontSize:10,marginTop:2}}>{when}</div>
                        </td>
                      )}
                      <td style={{padding:"4px 8px",textAlign:"center",borderRight:"1px solid #e5e5e5",color:isPRN(med.freq)?"#b45000":"#555",fontSize:10}}>
                        {isPRN(med.freq) ? `As Needed ${di+1}` : totalDoses>1 ? `Dose ${di+1}` : med.freq ? "Daily" : "As directed"}
                      </td>
                      {weeks[0].slice(0,2).map(d => (
                        <td key={d} style={{padding:"4px 8px",borderRight:"1px solid #eee",minWidth:60}}></td>
                      ))}
                    </tr>
                  ));
                })}
                {namedM.length > 4 && (
                  <tr style={{background:"#f0f6ff",cursor:"pointer"}} onClick={() => setShowAllPreview(s => !s)}>
                    <td colSpan={4} style={{padding:"7px 8px",fontSize:11,color:"#2563eb",fontWeight:600,textAlign:"center"}}>
                      {showAllPreview ? "▲ Show less" : `▼ Show ${namedM.length - 4} more medication${namedM.length-4!==1?"s":""}`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Donation nudge */}
      <div style={{background:"#f0f6ff",border:"1px solid #bfdbfe",borderRadius:"10px",padding:"12px 16px",marginBottom:"1rem",display:"flex",alignItems:"center",gap:12}}>
        <div style={{fontSize:22}}>💙</div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:"#1e3a5f",marginBottom:2}}>PillPaper is free to use</div>
          <div style={{fontSize:12,color:"#374151"}}>If this helped you or a loved one, a small Venmo tip keeps it running.</div>
        </div>
        <a href="https://venmo.com/pillpaper" target="_blank" rel="noopener noreferrer" style={{padding:"8px 14px",fontSize:12,fontWeight:600,background:"#2563eb",color:"#fff",borderRadius:"8px",textDecoration:"none",whiteSpace:"nowrap"}}>💙 Venmo</a>
      </div>

      {/* ── ABOUT — shown after app, earns its place ── */}
      <div style={{background:"#fff",border:"1px solid #e5e5e5",borderRadius:14,padding:"1.5rem",marginBottom:"1.5rem"}}>
        <div style={{fontSize:18,fontWeight:700,color:"#111",marginBottom:4,textAlign:"center"}}>Why PillPaper exists</div>
        <div style={{fontSize:13,color:"#888",textAlign:"center",marginBottom:16}}>A nurse's journey from paper charts to pill bottles — and why simple still wins.</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
          {[
            {icon:"📋", title:"30 years in healthcare", accent:"#2563eb", bg:"#f0f6ff", text:"I started as a CNA in 1993, worked as a bedside nurse in the paper chart era, and moved into clinical informatics in 2004. I've watched healthcare technology evolve for three decades. And somehow, the document patients go home with still hasn't caught up."},
            {icon:"👨‍👩‍👧‍👦", title:"Did you give the dog his pill?", accent:"#1a9e50", bg:"#dcfce7", text:"As a mom of four kids and many fur babies, I've stood in my own kitchen asking my husband that exact question. We needed something on the fridge, not an app with push notifications. A piece of paper. A checkbox. Done."},
            {icon:"💛", title:"The envelope that changed everything", accent:"#d97706", bg:"#fffbeb", text:"A friend going through cancer treatment had her family tracking pain med times on the corner of an envelope. I made her a spreadsheet. Her surgeon asked where she got it — said it was the clearest medication record he'd seen. That moment stayed with me."},
            {icon:"💊", title:"The pillbox I didn't trust", accent:"#7c3aed", bg:"#f5f3ff", text:"Helping care for my mother-in-law, I stared at her giant pill organizer full of compartments and felt uneasy. I didn't know what was morning or evening. What day was what. A printed record would have given everyone peace of mind — including me."},
            {icon:"❤️", title:"Same spreadsheet. Same result.", accent:"#dc2626", bg:"#fff5f5", text:"When my husband went through cancer treatment, I pulled out the same Excel file I'd made for my friend. Printed it. Wrote in the times by hand. In the middle of all that stress, it was one less thing to worry about. Simple. Clear. It worked."},
            {icon:"✨", title:"So I built it properly.", accent:"#0891b2", bg:"#f0fdff", text:"Alarms are nice. Reminders are nice. But sometimes you just need a piece of paper on the fridge that anyone can read and fill in — your spouse, your parent, your neighbor. Not everyone is tech-savvy. Not every moment calls for an app. PillPaper is that thing I always wished existed."},
          ].map(s => {
            const isOpen = !!expandedWhyCards[s.title];
            return (
              <div key={s.title} style={{borderRadius:10,border:"1px solid #eee",borderLeft:`4px solid ${s.accent}`,overflow:"hidden"}}>
                <button onClick={() => setExpandedWhyCards(prev => ({...prev, [s.title]: !prev[s.title]}))}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:s.bg,border:"none",cursor:"pointer",textAlign:"left"}}>
                  <span style={{fontSize:18,flexShrink:0}}>{s.icon}</span>
                  <span style={{flex:1,fontSize:13,fontWeight:700,color:"#111"}}>{s.title}</span>
                  <span style={{fontSize:12,color:"#888",flexShrink:0}}>{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div style={{padding:"10px 14px 12px 14px",background:"#fff",fontSize:12,color:"#555",lineHeight:1.7,borderTop:`1px solid #eee`}}>
                    {s.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{textAlign:"center",padding:"1rem",background:"linear-gradient(135deg,#eff6ff,#f0fdf4)",borderRadius:10,border:"1px solid #dbeafe"}}>
          <div style={{fontSize:14,fontWeight:600,color:"#1e3a5f",marginBottom:4}}>PillPaper is free because it should be.</div>
          <div style={{fontSize:12,color:"#555",lineHeight:1.6,maxWidth:500,margin:"0 auto"}}>
            No family should struggle to track medications during one of the hardest moments of their lives. If PillPaper helped you or someone you love, a small Venmo tip keeps it running.
          </div>
          <a href="https://venmo.com/pillpaper" target="_blank" rel="noopener noreferrer"
            style={{display:"inline-block",marginTop:12,padding:"8px 24px",fontSize:13,fontWeight:600,background:"#2563eb",color:"#fff",borderRadius:50,textDecoration:"none"}}>
            💙 Support on Venmo
          </a>
        </div>
      </div>

      {/* ── TESTIMONIALS ── */}
      {testimonials.length > 0 && (
        <div style={{background:"#f5f3ff",border:"1px solid #c4b5fd",borderRadius:16,padding:"3rem 1.5rem",marginTop:"2.5rem",marginBottom:"2.5rem"}}>
          <div style={{maxWidth:700,margin:"0 auto",textAlign:"center"}}>
            <div style={{fontSize:24,fontWeight:700,color:"#5b21b6",marginBottom:32}}>💬 What people are saying</div>
            
            {/* Rotating testimonial display */}
            <div style={{position:"relative",minHeight:180}}
              onMouseEnter={() => setTestimonialPaused(true)}
              onMouseLeave={() => setTestimonialPaused(false)}>
              
              {/* Left arrow */}
              {testimonials.length > 1 && (
                <button 
                  onClick={() => setTestimonialIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
                  style={{position:"absolute",left:-20,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,0.9)",border:"none",borderRadius:"50%",width:40,height:40,fontSize:20,cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.15)",zIndex:10,display:"flex",alignItems:"center",justifyContent:"center",color:"#7c3aed"}}>
                  ‹
                </button>
              )}
              
              {/* Right arrow */}
              {testimonials.length > 1 && (
                <button 
                  onClick={() => setTestimonialIndex(prev => (prev + 1) % testimonials.length)}
                  style={{position:"absolute",right:-20,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,0.9)",border:"none",borderRadius:"50%",width:40,height:40,fontSize:20,cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.15)",zIndex:10,display:"flex",alignItems:"center",justifyContent:"center",color:"#7c3aed"}}>
                  ›
                </button>
              )}
              
              {testimonials.map((t, i) => {
                const isActive = i === testimonialIndex;
                return (
                  <div key={i} style={{
                    position: i === 0 ? "relative" : "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? "translateY(0)" : "translateY(10px)",
                    transition: "opacity 0.5s ease, transform 0.5s ease",
                    pointerEvents: isActive ? "auto" : "none"
                  }}>
                    <div style={{background:"rgba(255,255,255,0.95)",borderRadius:16,padding:"2rem 2.5rem",boxShadow:"0 8px 32px rgba(0,0,0,0.15)"}}>
                      <div style={{fontSize:18,color:"#1f2937",lineHeight:1.7,marginBottom:20,fontStyle:"italic"}}>
                        "{t.quote}"
                      </div>
                      <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:12,fontSize:14}}>
                        <span style={{fontWeight:700,color:"#7c3aed"}}>{t.name || "Anonymous"}</span>
                        {t.role && (
                          <>
                            <span style={{color:"#d1d5db"}}>•</span>
                            <span style={{color:"#6b7280",fontStyle:"italic"}}>{t.role}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Dots indicator */}
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:24,marginBottom:24}}>
              {testimonials.map((_, i) => (
                <div key={i} 
                  onClick={() => setTestimonialIndex(i)}
                  style={{
                    width:8,
                    height:8,
                    borderRadius:"50%",
                    background: i === testimonialIndex ? "#fff" : "rgba(255,255,255,0.3)",
                    transition:"background 0.3s ease",
                    cursor:"pointer"
                  }}/>
              ))}
            </div>
            
            <button onClick={() => setShowShareFeedback(true)}
              style={{padding:"12px 28px",fontSize:14,fontWeight:600,background:"#fff",color:"#7c3aed",border:"1px solid #c4b5fd",borderRadius:50,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,0,0,0.1)"}}>
              ✨ Share Your Experience
            </button>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <div style={{marginTop:"2.5rem",padding:"1.5rem",borderTop:"1px solid #e5e5e5",textAlign:"center"}}>
        <div style={{fontSize:15,fontWeight:700,color:"#2563eb",marginBottom:4}}>PillPaper™</div>
        <div style={{fontSize:12,color:"#888",fontStyle:"italic",marginBottom:16}}>You fill your meds. We'll fill the paper.</div>
        <div style={{display:"flex",justifyContent:"center",gap:20,flexWrap:"wrap",marginBottom:12}}>
          <a href="https://venmo.com/pillpaper" target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:"#2563eb",textDecoration:"none",fontWeight:500}}>💙 Support on Venmo</a>
          <a onClick={() => setShowFeedback(true)} style={{fontSize:12,color:"#555",textDecoration:"none",cursor:"pointer"}}>✉️ Contact</a>
          <span style={{fontSize:12,color:"#555"}}>📍 Made in Pennsylvania</span>
        </div>
        <div style={{display:"flex",justifyContent:"center",gap:16,flexWrap:"wrap",marginBottom:12}}>
          <span onClick={() => setShowPrivacy(true)} style={{fontSize:11,color:"#2563eb",cursor:"pointer",textDecoration:"underline"}}>Privacy Policy</span>
          <span onClick={() => setShowTerms(true)} style={{fontSize:11,color:"#2563eb",cursor:"pointer",textDecoration:"underline"}}>Terms of Use</span>
          <span onClick={() => setShowBetaModal(true)} style={{fontSize:11,color:"#2563eb",cursor:"pointer",textDecoration:"underline"}}>About PillPaper</span>
        </div>
        <div style={{fontSize:11,color:"#bbb",lineHeight:1.6}}>
          © 2026 PillPaper™ — pillpaper.com. Patent Pending. All rights reserved.<br/>
          PillPaper is for personal tracking only and is not a substitute for medical advice.<br/>
          Always follow your doctor's or pharmacist's instructions.
        </div>
      </div>

      <Analytics />
    </div>
  );
}
